import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useToast } from '../../Toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { uploadSaenggibuAttachment } from '../../../firebase/storage';
import {
  SUPPORTED_UPLOAD_ACCEPT,
  isClearlyBlockedUploadFile,
} from '../../../constants/fileTypes';
import { analyzeExcelFile, analyzePastedBatchText, preCheckFiles } from '../../../api/chatApi';
import { getApiUrl } from '../../../utils/apiConfig';
import { getSaenggibuMaxBytes } from '../../../utils/neisBytes';
import { SAENGGIBU_BATCH_MAX_STUDENTS } from '../../../constants/fileLimits';
import { formatSourcePages, getReadGapMessages, getScanRangeReview } from '../../../utils/saenggibuScanRange';
import { trackSaenggibuUxEvent } from '../../../utils/eventTracker';
import FilePreCheckModal from '../../common/FilePreCheckModal';
import FieldHelpTooltip from '../../common/FieldHelpTooltip';
import Dialog from '../../common/Dialog';
import ModalActions from '../../common/ModalActions';
import Notice from '../../common/Notice';
import StudentRecognitionReviewList from '../../common/StudentRecognitionReviewList';
import ClassRosterCard, { useClassRosterLink } from './ClassRosterCard';
import BatchDuplicateStudentCard, { getMergedMetaItems } from './BatchDuplicateStudentCard';
import PriorSemesterOption from './PriorSemesterOption';
import ScoringImportModal from './ScoringImportModal';
import { getScoringHistory } from '../../../api/scoringApi';
import { CLASS_RECORDS_SOURCE, CLASS_TARGET_MAX_BYTES, classEvidenceReviewCounts } from '../../../utils/studentRecordsHandoff';
import { SINGLE_CATEGORIES, memoCategoryOf } from '../../../utils/studentMemos';
import ProUpgradeModal from '../ProUpgradeModal';
import SaenggibuAnalysisReviewModal from '../../saenggibu/SaenggibuAnalysisReviewModal';
import AchievementStandardPicker from '../../saenggibu/AchievementStandardPicker';
import SelectedAchievementStandardsField from '../../saenggibu/SelectedAchievementStandardsField';
import SaenggibuAttachMenu from '../../saenggibu/SaenggibuAttachMenu';
import { analyzeSaenggibuFileFromLibrary } from '../../../api/saenggibuApi';
import { LIBRARY_SOURCE, buildLibraryAttachmentDescriptor } from '../../../utils/saenggibuLibraryPick';
import {
  mergeSelectedStandards,
  recordIdentity,
  toAchievementStandardRefs,
  toRecordSummary,
} from '../../saenggibu/achievementStandardPicker.utils';
import {
  droppedStandardsNotice,
  refsFromHistoryRecord,
  restoreValidatedStandards,
} from '../../saenggibu/achievementStandardRestore';
import {
  buildRecognitionExportRows,
  getRecognitionExportDate,
  writeRecognitionWorkbook,
} from '../../../utils/recognitionReview';
import {
  buildPlanItemNames,
  buildPlanSelectionMeta,
  listPlanItems,
  planItemSuffix,
  shouldPickBatchPlanItem,
} from '../../../utils/evalPlanSelection';
import './Step1_ExcelUpload.css';

const BATCH_REFERENCE_MAX_FILES = 5;
const BATCH_REFERENCE_TOTAL_LIMIT_BYTES = 50 * 1024 * 1024;
const BATCH_STORAGE_FILE_LIMIT_BYTES = 50 * 1024 * 1024;

const collectAnalysisWarnings = (analysisResult) => {
  const warnings = Array.isArray(analysisResult?.warnings)
    ? analysisResult.warnings.filter(Boolean)
    : [];
  if (analysisResult?.was_truncated) {
    warnings.push('파일 내용이 길어 일부만 분석했어요. 학생별 원문을 확인해주세요.');
  }
  return [...new Set(warnings)];
};

const BATCH_CATEGORY_GUIDES = {
  행동발달: {
    title: '학생별 기록 파일을 그대로 올려주세요',
    description: '학생 이름이 있는 표면 열 이름이 달라도 AI가 학생별 행동 근거로 묶어요.',
    examples: ['누가기록', '상담 메모', '행동 관찰표'],
    contentLabel: '관찰내용',
    assistTitle: '행동 특성이 보이면 충분해요',
    assistDescription: '학급 생활, 교우관계, 수업 태도, 상담 메모처럼 학생별 행동이 적힌 파일을 그대로 올려주세요.',
    templateRows: [
      ['번호', '학생명', '관찰내용'],
      ['1', '김왓퀴', '학급 회의에서 친구 의견을 먼저 듣고 정리해 발표함. 역할 분담이 필요한 상황에서 조용히 필요한 일을 맡아 마무리함.'],
      ['2', '이퀴즈', '과제 준비물을 자주 챙기며 모둠 활동에서 자료 조사와 정리를 담당함. 발표 전 친구가 어려워하는 부분을 설명해 줌.'],
      ['3', '박생기', '수업 중 질문을 통해 내용을 확인하고 자신의 생각을 구체적으로 말함. 갈등 상황에서 감정을 가라앉히고 대화로 해결하려는 모습을 보임.']
    ]
  },
  교과: {
    title: '교과세특 학생 제출물·기록 파일을 그대로 올려주세요',
    description: '보고서, 수행평가물, 활동지처럼 학생별 제출물이 이어진 파일도 학생별 근거로 나눠 읽어요.',
    examples: ['수행평가', '탐구 보고서', '활동지', '발표 기록'],
    contentLabel: '교과 활동 기록',
    assistTitle: '학생이 실제로 낸 자료가 가장 좋아요',
    assistDescription: '수행평가, 탐구 보고서, 발표 내용, 수업 활동지처럼 학생이 무엇을 조사하고 설명했는지 보이는 자료를 넣어주세요.',
    templateRows: [
      ['번호', '학생명', '교과 활동 기록'],
      ['1', '김왓퀴', '국어 토론 활동에서 근거 자료를 찾아 주장과 연결해 발표함. 모둠 글쓰기에서는 문단 구조를 정리하고 친구 의견을 반영함.'],
      ['2', '이퀴즈', '수학 수행평가에서 함수 그래프의 특징을 표로 정리하고 실생활 상황에 적용해 설명함. 풀이 과정을 또래에게 차분히 안내함.'],
      ['3', '박생기', '과학 탐구 활동에서 실험 변인을 구분하고 결과를 그래프로 나타냄. 관찰 내용을 바탕으로 결론을 수정하며 탐구 과정을 보완함.']
    ]
  },
  창체: {
    title: '창체 학생 활동표를 그대로 올려주세요',
    description: '활동명, 역할, 기여 내용처럼 열이 나뉘어 있어도 학생별 근거로 묶어요.',
    examples: ['자율활동', '행사 기록', '봉사 기록'],
    contentLabel: '창체 활동 기록',
    assistTitle: '참여한 활동과 역할이 보이면 됩니다',
    assistDescription: '자율활동, 학교 행사, 봉사활동, 캠페인 참여처럼 학생별 역할과 태도가 드러나는 자료를 올려주세요.',
    templateRows: [
      ['번호', '학생명', '창체 활동 기록'],
      ['1', '김왓퀴', '학급 자치회의에서 의견을 정리하고 실천 계획을 제안함. 행사 준비 과정에서 필요한 역할을 먼저 맡아 진행을 도움.'],
      ['2', '이퀴즈', '환경 캠페인에서 자료 조사와 안내 문구 작성을 담당함. 활동 후 개선점을 정리해 다음 캠페인 방향을 제안함.'],
      ['3', '박생기', '봉사활동에서 맡은 구역을 꾸준히 관리하고 친구들이 참여할 수 있도록 역할을 나누어 안내함.']
    ]
  },
  진로: {
    title: '진로 학생 기록 파일을 그대로 올려주세요',
    description: '희망 진로, 관심 분야, 탐색 활동 열이 나뉘어 있어도 학생별로 정리해요.',
    examples: ['탐구 보고서', '희망 진로', '상담 기록'],
    contentLabel: '진로 활동 기록',
    assistTitle: '관심 분야와 탐구 과정이 보이면 좋아요',
    assistDescription: '진로 탐구 보고서, 진로 상담 기록, 희망 학과·직업 메모처럼 학생의 관심과 활동 과정이 있는 자료를 넣어주세요.',
    templateRows: [
      ['번호', '학생명', '진로 활동 기록'],
      ['1', '김왓퀴', '생명과학 분야에 관심을 두고 유전자 치료 사례를 조사함. 관련 기사와 논문 요약을 바탕으로 장단점을 비교해 발표함.'],
      ['2', '이퀴즈', '교육 분야 진로를 희망하며 수업 보조 활동에 참여함. 또래에게 개념을 설명하는 과정에서 쉬운 예시를 만들어 전달함.'],
      ['3', '박생기', '환경공학 관련 진로 탐색 보고서를 작성함. 지역 하천 오염 사례를 조사하고 해결 방안을 정책과 기술 측면에서 정리함.']
    ]
  },
  동아리: {
    title: '동아리 학생 활동 기록을 그대로 올려주세요',
    description: '동아리명, 역할, 활동 내용 열이 나뉘어 있어도 학생별 근거로 묶어요.',
    examples: ['활동 보고서', '역할 기록', '산출물 메모'],
    contentLabel: '동아리 활동 기록',
    assistTitle: '역할과 실제 활동이 보이면 충분해요',
    assistDescription: '동아리명, 맡은 역할, 탐구·제작·발표 과정처럼 학생이 실제로 한 일이 드러나는 자료를 올려주세요.',
    templateRows: [
      ['번호', '학생명', '동아리 활동 기록'],
      ['1', '김왓퀴', '과학탐구반에서 실험 기록을 정리하고 센서 배치를 제안함. 모둠 발표 자료를 나눠 맡아 결과 해석을 보완함.'],
      ['2', '이퀴즈', '독서토론반에서 발제문을 준비하고 친구 의견의 공통점을 정리함. 다음 모임 주제를 제안해 역할을 나눔.'],
      ['3', '박생기', '환경동아리에서 캠페인 문구를 작성하고 분리배출 안내를 담당함. 활동 후 개선점을 정리해 다음 실천 계획을 제안함.']
    ]
  },
  교과_고등: {
    title: '고등 세특 학생 제출물을 그대로 올려주세요',
    description: '탐구 보고서, 수행평가물, 발표 자료처럼 학생별 산출물이 이어진 파일도 나눠 읽어요.',
    examples: ['탐구 보고서', '수행평가', '발표 자료'],
    contentLabel: '학생 제출물/활동 내용',
    assistTitle: '학생이 실제로 낸 자료가 가장 좋아요',
    assistDescription: '고등 탐구 보고서, 수행평가 산출물, 발표 내용처럼 주제와 사고 과정이 보이는 자료를 넣어주세요.',
    templateRows: [
      ['번호', '학생명', '학생 제출물/활동 내용'],
      ['1', '김왓퀴', '최저임금 통계를 비교해 청소년 노동 인권 보고서를 작성함. 기사와 수치를 근거로 쟁점을 정리하고 후속 질문을 남김.'],
      ['2', '이퀴즈', '행렬을 이용해 교통량 변화를 모형화함. 가정과 오차 원인을 구분하고 다른 변수로 다시 계산함.'],
      ['3', '박생기', '지역 하천 수질 자료를 그래프로 정리하고 정책 사례와 비교함. 탐구 한계를 적어 다음 조사 방향을 제안함.']
    ]
  }
};

export const BATCH_SUPPORTED_CATEGORIES = ['행동발달', '교과', '교과_고등', '창체', '동아리', '진로'];
export const BATCH_REFERENCE_CATEGORIES = ['교과', '교과_고등', '동아리'];
export const BATCH_MULTI_FILE_CATEGORIES = ['교과', '교과_고등', '창체', '진로', '동아리'];

export const getBatchCategoryLabel = (category) => {
  if (category === '교과') return '교과세특';
  if (category === '교과_고등') return '세특(고)';
  return category;
};

// 학급 관리 영역 이름(행동특성·교과세특·자율·동아리·진로). 메모를 고른 기준을 알릴 때 쓴다.
const categoryLabelOf = category => SINGLE_CATEGORIES
  .find(item => item.category === memoCategoryOf(category))?.label || getBatchCategoryLabel(category);

export const isBatchReferenceCategory = (category) => (
  BATCH_REFERENCE_CATEGORIES.includes(category)
);

/**
 * 참고자료의 성격. 동아리 계획서와 평가계획서는 서로 대체할 수 없다.
 * 교과와 교과_고등은 같은 평가계획서를 쓰므로 전환해도 유지한다.
 */
export const getBatchReferenceKind = (category) => {
  if (category === '동아리') return 'club_plan';
  if (category === '교과' || category === '교과_고등') return 'evaluation_plan';
  return null;
};

export const getReferenceCopy = (category) => {
  if (category === '동아리') {
    return {
      title: '동아리 연간 계획서는 어떻게 쓰이나요?',
      items: [
        '동아리 연간 계획서는 목표와 활동 맥락을 잡는 참고자료로만 사용해요.',
        '실제 생기부 문장의 핵심 근거는 학생별 활동 기록이에요.',
        '파일을 올리면 분석 내용을 먼저 확인한 뒤 참고자료로 넣을 수 있어요.',
      ],
      shortLabel: '동아리 계획서',
      cardTitle: '동아리 계획서 추가',
      uploadLabel: '동아리 계획서 파일 첨부',
      analyzingLabel: '동아리 계획서 분석 중...',
      placeholder: '파일이 없으면 동아리명, 연간 활동, 목표를 붙여넣어도 돼요.',
      badge: '동아리 계획서 참고',
      toast: '동아리 계획서를 참고자료로 추가했어요. 이제 학생별 활동 기록을 올려주세요.',
      emptyError: '동아리 계획서에서 동아리명이나 활동 계획을 찾지 못했어요.',
      analyzeError: '동아리 계획서를 분석하지 못했어요.',
      addError: '동아리 계획서 파일을 추가하지 못했어요.',
      limitError: (max) => `동아리 계획서는 최대 ${max}개까지 넣을 수 있어요.`,
      sizeError: '동아리 계획서 파일 합계가 50MB를 넘었어요. 파일 수나 용량을 줄여주세요.',
      reviewTitle: (count) => (count > 1 ? 'AI가 동아리 계획서들을 이렇게 합쳐 읽었어요' : 'AI가 동아리 계획서를 이렇게 읽었어요'),
      categoryLabel: '동아리 참고자료',
      editLabel: '동아리 계획서 내용 수정',
      fieldLabel: '동아리 계획서 참고자료',
      restoreName: '이전 동아리 계획서 내용',
      analysisCategory: '동아리',
    };
  }
  return {
    title: '평가계획서는 어떻게 쓰이나요?',
    items: [
      '평가계획서는 성취기준, 평가요소, 채점기준을 잡는 참고자료로만 사용해요.',
      '실제 생기부 문장의 핵심 근거는 학생별 기록 파일이에요.',
      '파일을 올리면 분석 내용을 먼저 확인한 뒤 참고자료로 넣을 수 있어요.',
    ],
    shortLabel: '평가계획서',
    cardTitle: '평가계획서 추가',
    uploadLabel: '평가계획서 파일 첨부',
    analyzingLabel: '평가계획서 분석 중...',
    placeholder: '파일이 없으면 성취기준, 평가요소, 채점기준을 붙여넣어도 돼요.',
    badge: '평가계획서 참고',
    toast: '평가계획서를 참고자료로 추가했어요. 이제 학생별 기록 파일을 올려주세요.',
    emptyError: '평가계획서에서 성취기준이나 평가요소를 찾지 못했어요.',
    analyzeError: '평가계획서를 분석하지 못했어요.',
    addError: '평가계획서 파일을 추가하지 못했어요.',
    limitError: (max) => `평가계획서는 최대 ${max}개까지 넣을 수 있어요.`,
    sizeError: '평가계획서 파일 합계가 50MB를 넘었어요. 파일 수나 용량을 줄여주세요.',
    reviewTitle: (count) => (count > 1 ? 'AI가 평가계획서들을 이렇게 합쳐 읽었어요' : 'AI가 평가계획서를 이렇게 읽었어요'),
    categoryLabel: category === '교과_고등' ? '고등 세특 참고자료' : '교과세특 참고자료',
    editLabel: '평가계획서 내용 수정',
    fieldLabel: '평가계획서 참고자료',
    restoreName: '이전 평가계획서 내용',
    analysisCategory: category === '교과_고등' ? '교과_고등' : '교과',
  };
};

export const buildClubReferenceDraft = (data = {}) => {
  const lines = [];
  addReferenceDraftSection(lines, '동아리명', data.club_name);
  addReferenceDraftSection(lines, '유형', data.club_type);
  addReferenceDraftSection(lines, '활동 시간', data.meeting_schedule);
  addReferenceDraftSection(lines, '목표', data.objectives);
  addReferenceDraftSection(lines, '주요 활동', data.main_activities);
  if (Array.isArray(data.yearly_plan) && data.yearly_plan.length > 0) {
    const planText = data.yearly_plan.map((item) => {
      const month = item?.month || '';
      const activities = Array.isArray(item?.activities) ? item.activities.join(', ') : toReferenceAnalysisText(item?.activities);
      return [month, activities].filter(Boolean).join(': ');
    }).filter(Boolean).join('\n');
    addReferenceDraftSection(lines, '연간 계획', planText);
  }
  addReferenceDraftSection(lines, '기대 성과', data.expected_outcomes);
  addReferenceDraftSection(lines, '요약', data.summary);
  return lines.join('\n\n').trim();
};

const PASTE_GRID_INITIAL_ROWS = 20;

const KOREAN_SURNAMES = '김이박최정강조윤장임한오서신권황안송류전홍고문양손배백허유남심노하곽성차주우구민진나지엄변채원천방공현함염여추도소석선설마길연위표명기반왕금옥육인맹제모탁국어은편용예봉경';
const FILENAME_NON_NAME_WORDS = /(보고서|활동지|평가|수행|과제|제출|최종|수정|자료|발표|탐구|활동|결과|계획|감상|독후|포트폴리오|학년|학기|교과|세특|양식|파일|사본|스캔|정리|복사|병합|원본|작성|학생|이름|성명)/;

const looksLikeFilenameName = (token) => (
  token.length >= 2
  && token.length <= 4
  && KOREAN_SURNAMES.includes(token[0])
  && !FILENAME_NON_NAME_WORDS.test(token)
);

// 이름처럼 보이는 조각이 하나로 좁혀질 때만 돌려준다 (학번 바로 뒤 조각 → 세 글자 조각 순).
export const extractStudentNameFromFilename = (filename = '') => {
  const baseName = String(filename)
    .normalize('NFC')
    .replace(/\.[^.]+$/, '')
    .replace(/merged/gi, '');
  const candidates = baseName
    .split(/[_\-\s().,[\]{}+#&]+/)
    .map((part) => part.match(/^(\d{3,8})?([가-힣]{2,4})$/))
    // 두 글자 조각('윤리', '정보')은 과목명과 헷갈려 학번 바로 뒤에 올 때만 이름으로 본다.
    .filter((match) => match && looksLikeFilenameName(match[2]) && (match[2].length >= 3 || match[1]))
    .map((match) => ({ name: match[2], afterNumber: Boolean(match[1]) }));
  const pick = (list) => {
    const unique = [...new Set(list.map((item) => item.name))];
    return unique.length === 1 ? unique[0] : '';
  };
  return pick(candidates.filter((item) => item.afterNumber))
    || pick(candidates)
    || pick(candidates.filter((item) => item.name.length === 3));
};

export const isWeakDetectedStudentName = (name = '') => {
  const normalized = String(name).trim();
  return !normalized
    || /^학생\s*\d*$/i.test(normalized)
    || /^(미상|알\s*수\s*없음|이름\s*(없음|미상)|없음|unknown)/i.test(normalized)
    || normalized.includes('내용 없음');
};

// 백엔드가 파일명까지 보고 AI 로 이름을 판단했으면(name_recovery.mode === 'ai') 파일명 규칙으로 덮지 않는다.
// 파일명 규칙은 백엔드가 regex/off 이거나 이 필드를 모르는 구버전일 때만 쓴다.
export const resolveMultiFileStudentName = ({ studentName, filename, fileMeta, fallbackIndex }) => {
  const filenameName = fileMeta?.name_recovery?.mode === 'ai' ? '' : extractStudentNameFromFilename(filename);
  if (isWeakDetectedStudentName(studentName) && filenameName) return filenameName;
  return studentName || filenameName || `학생${fallbackIndex}`;
};

const BATCH_HELP_TEXT = {
  upload: (guide) => ({
    title: '어떤 자료를 올리면 좋나요?',
    items: [
      guide.assistDescription,
      '학생명 열이 있으면 나머지 의미 있는 열은 AI가 학생별 참고 내용으로 묶어요.',
      '엑셀, PDF, PPT, 한글, 이미지 파일을 사용할 수 있어요.',
    ],
  }),
  paste: (guide) => ({
    title: '엑셀 표는 어떻게 붙여넣나요?',
    items: [
      '엑셀에서 표 전체를 복사해 아래 첫 칸에 붙여넣으세요.',
      `학생명 열만 찾으면 나머지 열은 ${guide.contentLabel}으로 자동 정리해요.`,
      '파일이 없어도 표 붙여넣기만으로 생성할 수 있어요.',
    ],
  }),
};

const toReferenceAnalysisText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  if (Array.isArray(value)) {
    return value
      .map(toReferenceAnalysisText)
      .filter(Boolean)
      .join('\n');
  }
  if (typeof value === 'object') {
    const preferredKeys = ['name', 'title', 'label', 'level', 'criteria', 'description', 'content', 'score', 'max_points'];
    const preferredParts = preferredKeys
      .map(key => toReferenceAnalysisText(value[key]))
      .filter(Boolean);

    if (preferredParts.length > 0) {
      return preferredParts.join(' / ');
    }

    return Object.entries(value)
      .slice(0, 6)
      .map(([key, itemValue]) => {
        const text = toReferenceAnalysisText(itemValue);
        return text ? `${key}: ${text}` : '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return '';
};

const pickReferenceAnalysisText = (data = {}, keys = []) => {
  for (const key of keys) {
    const text = toReferenceAnalysisText(data[key]);
    if (text) return text;
  }
  return '';
};

const addReferenceDraftSection = (lines, label, value) => {
  const text = toReferenceAnalysisText(value);
  if (text) lines.push(`[${label}]\n${text}`);
};

const createReferenceSummaryItem = (label, value) => {
  const text = toReferenceAnalysisText(value);
  return text ? { label, value: text } : null;
};

const buildReferenceSummaryItems = (data = {}, category = '교과') => {
  if (category === '동아리') {
    return [
      createReferenceSummaryItem('동아리명', data.club_name),
      createReferenceSummaryItem('유형', data.club_type),
      createReferenceSummaryItem('목표', data.objectives),
      createReferenceSummaryItem('주요 활동', data.main_activities),
      createReferenceSummaryItem('요약', data.summary),
    ].filter(Boolean);
  }
  return [
    createReferenceSummaryItem('과목', data.subject),
    createReferenceSummaryItem('활동명', pickReferenceAnalysisText(data, ['activity_name', 'name'])),
    createReferenceSummaryItem('성취기준', pickReferenceAnalysisText(data, ['achievement_standard', 'achievement_standards', 'achievement'])),
    createReferenceSummaryItem('단원', data.unit),
    createReferenceSummaryItem('영역', data.domain),
    createReferenceSummaryItem('평가방법', pickReferenceAnalysisText(data, ['evaluation_method', 'method', 'assessment_method'])),
    createReferenceSummaryItem('평가요소', pickReferenceAnalysisText(data, ['evaluation_items', 'evaluation_elements', 'assessment_elements', 'evaluation_item'])),
    createReferenceSummaryItem('평가기준', pickReferenceAnalysisText(data, ['evaluation_criteria', 'criteria', 'rubric', 'scoring_criteria', 'scoring_levels'])),
    createReferenceSummaryItem('평가내용', pickReferenceAnalysisText(data, ['evaluation_content', 'assessment_content', 'task', 'task_description'])),
    createReferenceSummaryItem('평가시기', data.evaluation_period),
    createReferenceSummaryItem('평가비율', data.evaluation_ratio),
  ].filter(Boolean);
};

const INSTRUCTION_HELP_TEXT = {
  title: '어떤 요청을 적으면 좋나요?',
  items: [
    '원하는 문체나 톤을 적어도 돼요. 예: 담백하게, 너무 과장하지 않게, 교사 관찰 문체로.',
    '원하는 패턴을 적어도 돼요. 예: 탐구 과정 → 근거 → 수업 역량 흐름으로.',
    '좋게 쓴 예시 문장을 붙이고 “이런 패턴으로 써줘”라고 요청해도 돼요.',
    '학생 기록에 없는 사실을 만들거나 기재요령을 어기는 요청은 반영하지 않아요.',
  ],
};

const createEmptyPasteRows = (count = PASTE_GRID_INITIAL_ROWS) => (
  Array.from({ length: count }, (_, index) => ({
    no: String(index + 1),
    name: '',
    content: ''
  }))
);

const NAME_HEADERS = ['이름', '학생명', '성명', '학생 이름', '학생성명', '명렬', '명단'];
const ID_HEADERS = ['번호', '순번', '연번', '학번', '출석번호', 'no', 'num', 'number'];
const IGNORED_HEADERS = [...ID_HEADERS, ...NAME_HEADERS, '반', '학년', '학급', '구분', '상태'];
const TEACHER_INSTRUCTION_PRESETS = {
  행동발달: [
    {
      label: '담백한 공식 문체',
      instruction: '과장된 칭찬보다 교사가 실제 관찰한 행동을 담백한 공식 문체로 정리해주세요.'
    },
    {
      label: '구체 행동 중심',
      instruction: '성실함, 책임감 같은 추상어만 쓰지 말고 학급 생활에서 보인 구체 행동과 관계 속 기여가 드러나게 써주세요.'
    },
    {
      label: '아쉬운 점 자연스럽게',
      instruction: '장점만 과하게 쓰지 말고 보완이 필요한 모습도 부정적으로 단정하지 않게, 성장 가능성과 함께 자연스럽게 반영해주세요.'
    },
  ],
  교과: [
    {
      label: '수업 과정 중심',
      instruction: '결과만 나열하지 말고 수업 중 질문, 탐구 과정, 자료 해석, 발표 준비처럼 학습 과정이 보이게 써주세요.'
    },
    {
      label: '평가 기준 반영',
      instruction: '평가계획서의 성취기준과 평가요소를 참고하되, 학생별 기록의 구체 사실을 중심으로 세특을 작성해주세요.'
    },
    {
      label: '패턴 예시 기반',
      instruction: '좋은 세특처럼 "무엇을 탐구했는지 → 어떤 근거로 생각했는지 → 수업 역량이 어떻게 드러났는지" 흐름으로 써주세요.'
    },
    {
      label: '진로 연결',
      instruction: '학생 기록에 진로나 관심 분야가 있으면 교과 활동과 자연스럽게 연결하되, 진로를 확정한 것처럼 단정하지 말아주세요.'
    },
  ],
  창체: [
    {
      label: '역할과 기여 중심',
      instruction: '활동 참여 사실만 쓰지 말고 맡은 역할, 실제 기여, 공동체에 준 영향이 보이게 써주세요.'
    },
    {
      label: '활동명보다 과정',
      instruction: '행사명이나 활동명 나열보다 준비 과정, 협업, 문제 해결, 후속 실천이 드러나게 정리해주세요.'
    },
  ],
  진로: [
    {
      label: '탐색 과정 중심',
      instruction: '희망 직업을 단정하기보다 관심 분야를 탐색한 과정, 조사한 내용, 후속 질문이 보이게 써주세요.'
    },
    {
      label: '관심 분야 연결',
      instruction: '학생의 관심 분야와 활동 내용을 자연스럽게 연결하되, 내면의 다짐이나 의지를 추측하지 말아주세요.'
    },
  ],
  동아리: [
    {
      label: '역할과 기여 중심',
      instruction: '참여 사실만 쓰지 말고 동아리에서 맡은 역할, 실제 기여, 산출물이 보이게 써주세요.'
    },
    {
      label: '과정 중심',
      instruction: '결과나 수상보다 준비 과정, 협력, 문제 해결이 드러나게 정리해주세요.'
    },
  ],
  교과_고등: [
    {
      label: '탐구 과정 중심',
      instruction: '결과만 나열하지 말고 의문, 근거, 비교·분석, 후속 탐구가 보이게 써주세요.'
    },
    {
      label: '평가 기준 반영',
      instruction: '평가계획서의 성취기준을 참고하되, 학생 제출물의 구체 사실을 중심으로 세특을 작성해주세요.'
    },
    {
      label: '진로 연결',
      instruction: '학생 자료에 진로나 관심 분야가 있으면 교과 활동과 자연스럽게 연결하되, 진로를 확정한 것처럼 단정하지 말아주세요.'
    },
  ],
};

const normalizeHeader = (value) => String(value || '').replace(/\s+/g, '').trim().toLowerCase();

const findHeaderIndex = (headers, candidates) => {
  const normalizedCandidates = candidates.map(normalizeHeader);
  return headers.findIndex(header => normalizedCandidates.some(candidate => normalizeHeader(header).includes(candidate)));
};

const isIgnoredHeader = (header) => {
  const normalized = normalizeHeader(header);
  if (!normalized) return true;
  return IGNORED_HEADERS.some(candidate => {
    const normalizedCandidate = normalizeHeader(candidate);
    return normalized === normalizedCandidate || normalized.includes(normalizedCandidate);
  });
};

const findBestHeaderRowIndex = (rows) => {
  const maxScanRows = Math.min(rows.length, 5);
  for (let index = 0; index < maxScanRows; index += 1) {
    const row = rows[index];
    const nameIndex = findHeaderIndex(row, NAME_HEADERS);
    if (nameIndex >= 0) return index;
  }

  return -1;
};

const normalizePastedRows = (text) => {
  try {
    const workbook = XLSX.read(text, { type: 'string' });
    const firstSheetName = workbook.SheetNames[0];
    if (firstSheetName) {
      const rows = XLSX.utils
        .sheet_to_json(workbook.Sheets[firstSheetName], { header: 1, raw: false, defval: '' })
        .map(row => row.map(cell => String(cell || '').trim()))
        .filter(row => row.some(cell => cell));
      if (rows.length > 0) return rows;
    }
  } catch {
    // Fall through to lightweight TSV/CSV parsing.
  }

  return text
    .split(/\r?\n/)
    .map(line => {
      const delimiter = line.includes('\t') ? '\t' : ',';
      return line.split(delimiter).map(cell => String(cell || '').trim());
    })
    .filter(row => row.some(cell => cell));
};

const shouldHydrateGridFromClipboard = (text) => {
  const normalized = String(text || '').trim();
  if (!normalized) return false;

  const rows = normalizePastedRows(normalized);
  if (rows.length <= 1) return false;

  const maxColumns = Math.max(...rows.map(row => row.length));
  const hasHeader = findBestHeaderRowIndex(rows) >= 0;

  return hasHeader || maxColumns >= 2;
};

export const parseStudentsFromPastedTable = (text) => {
  const rows = normalizePastedRows(text);
  if (rows.length === 0) {
    return { students: [], reason: 'empty' };
  }

  const headerRowIndex = findBestHeaderRowIndex(rows);

  const hasHeader = headerRowIndex >= 0;
  const headers = hasHeader ? rows[headerRowIndex] : [];
  const dataRows = rows.slice(hasHeader ? headerRowIndex + 1 : 0);

  let nameIndex = hasHeader ? findHeaderIndex(headers, NAME_HEADERS) : -1;
  let contentIndexes = hasHeader
    ? headers
        .map((header, index) => ({ header, index }))
        .filter(({ index }) => index !== nameIndex)
        .filter(({ header }) => !isIgnoredHeader(header))
        .map(({ index }) => index)
    : [];

  if (!hasHeader) {
    const maxColumns = Math.max(...rows.map(row => row.length));
    if (maxColumns >= 3) {
      nameIndex = 1;
      contentIndexes = Array.from({ length: maxColumns - 2 }, (_, index) => index + 2);
    } else if (maxColumns >= 2) {
      nameIndex = 0;
      contentIndexes = [1];
    }
  }

  if (nameIndex < 0 || contentIndexes.length === 0) {
    return { students: [], reason: 'unmatched_columns' };
  }

  const students = dataRows
    .map((row, index) => {
      const name = String(row[nameIndex] || '').trim();
      const includedColumns = [];
      const content = contentIndexes
        .map(contentIndex => {
          const value = String(row[contentIndex] || '').trim();
          if (!value) return '';
          const header = hasHeader
            ? String(headers[contentIndex] || `열 ${contentIndex + 1}`).trim()
            : '';
          if (header) includedColumns.push(header);
          return header ? `${header}: ${value}` : value;
        })
        .filter(Boolean)
        .join('\n');
      const warnings = [];

      if (!name) warnings.push('이름을 명확히 찾지 못했어요.');
      if (content.length < 20) warnings.push('내용이 짧아요.');

      return {
        id: `pasted_${index + 1}`,
        name: name || `학생${index + 1}`,
        existing_content: content,
        content,
        characteristics: content ? content.slice(0, 42) : '내용 확인 필요',
        included_columns: includedColumns,
        confidence: hasHeader ? (warnings.length ? 0.72 : 0.95) : (warnings.length ? 0.6 : 0.78),
        warnings
      };
    })
    .filter(student => student.name || student.existing_content);

  return {
    students,
    hasHeader,
    detectedColumns: hasHeader
      ? headers.filter((header, index) => header && index !== nameIndex && contentIndexes.includes(index))
      : ['이름', '관찰내용'],
    nameColumn: hasHeader ? headers[nameIndex] : '이름',
    contentColumnCount: contentIndexes.length,
    reason: students.length > 0 ? 'parsed' : 'no_students'
  };
};

const buildGridRowsFromPastedText = (text) => {
  const parsed = parseStudentsFromPastedTable(text);
  if (parsed.students.length > 0) {
    const rows = parsed.students.map((student, index) => ({
      no: String(index + 1),
      name: student.name || '',
      content: student.existing_content || student.content || ''
    }));
    return rows.length < PASTE_GRID_INITIAL_ROWS
      ? [...rows, ...createEmptyPasteRows(PASTE_GRID_INITIAL_ROWS - rows.length).map((row, idx) => ({ ...row, no: String(rows.length + idx + 1) }))]
      : rows;
  }

  return createEmptyPasteRows();
};

const gridRowsToText = (rows, contentLabel = '관찰내용') => {
  const filledRows = rows.filter(row => row.name.trim() || row.content.trim());
  if (filledRows.length === 0) return '';

  return [
    ['번호', '학생명', contentLabel].join('\t'),
    ...filledRows.map((row, index) => [
      row.no || String(index + 1),
      row.name,
      row.content
    ].join('\t'))
  ].join('\n');
};

// 평가계획서에서 평가를 하나 골랐는지(single)·모두 합쳤는지(merge). 참고자료가 바뀌면 함께 지운다.
const withoutPlanPick = ({ evalPlanMode, evalUnitDetectedCount, ...rest }) => rest;

const createDefaultBatchOptions = () => ({
  category: '행동발달',  // 행동발달, 교과, 교과_고등, 창체, 동아리, 진로
  wordCount: 1500,       // 바이트
  teacherInstruction: '',
  referenceText: '',
  referenceFiles: [],
  achievementStandards: [],
});

// 결과 단계에서 돌아오거나 "다음 학급 만들기"로 재마운트될 때 이어받는 설정 스냅샷.
// Step1 은 2단계 이후 언마운트되므로, 종류·바이트·지시·참고자료·성취기준은 상위(BatchProcessingContainer)가 들고 있다가 돌려준다.
const resolvePersistedBatchOptions = (persisted) => {
  const safe = persisted && typeof persisted === 'object' ? persisted : null;
  const options = safe?.options && typeof safe.options === 'object'
    ? { ...createDefaultBatchOptions(), ...safe.options }
    : createDefaultBatchOptions();
  return {
    options,
    teacherInstruction: typeof safe?.teacherInstruction === 'string'
      ? safe.teacherInstruction
      : (options.teacherInstruction || ''),
    referenceFile: safe?.referenceFile && typeof safe.referenceFile === 'object' ? safe.referenceFile : null,
    pickerSchoolLevel: safe?.pickerSchoolLevel === '고등' || safe?.pickerSchoolLevel === '초등' || safe?.pickerSchoolLevel === '중등'
      ? safe.pickerSchoolLevel
      : '중등',
  };
};

const Step1_ExcelUpload = ({
  onNext,
  onBack,
  uploadedData,
  initialBatchState,
  onCategoryChange,
  persistedOptions = null,
  onOptionsPersist,
}) => {
  const { showToast } = useToast();
  const [initialPersisted] = useState(() => resolvePersistedBatchOptions(persistedOptions));
  // 결과 단계에서 돌아와 이전 설정을 이어받은 마운트인지 (첫 진입은 false). 상단 안내 칩 표시용.
  // seedOnly(단일에서 종류만 승계)는 "이전 설정" 이 아니므로 칩을 띄우지 않는다.
  const [continuedFromPrevious] = useState(() => Boolean(persistedOptions?.options) && !persistedOptions?.seedOnly);
  const { currentUser } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const isProUser = subscriptionStatus?.isPro || false;
  const fileInputRef = useRef(null);
  const referenceFileInputRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const uploadAbortControllerRef = useRef(null);
  const uploadCancelRequestedRef = useRef(false);
  
  // PRO 모달 상태
  const [showProModal, setShowProModal] = useState(false);
  
  const [isDragOver, setIsDragOver] = useState(false);
  const [isReferenceDragOver, setIsReferenceDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [remainingTime, setRemainingTime] = useState(60); // 1분 표시
  // 결과 단계에서 "입력으로 돌아가기"로 재마운트되면 uploadedData 에 분석 결과(학생 목록)가 들어 있다.
  // 학생 목록까지 함께 복원해야 "파일 카드만 남고 학생 0명·생성 버튼 없음" 인 막힌 화면이 되지 않는다.
  // 학생 목록이 없는 uploadedData 는 복원하지 않는다(파일 카드만 남기면 같은 막힌 화면이 된다).
  const [restoredUpload] = useState(() => (
    Array.isArray(uploadedData?.analysisResult?.students) && uploadedData.analysisResult.students.length > 0
      ? uploadedData
      : null
  ));
  const [uploadedFile, setUploadedFile] = useState(restoredUpload);
  const [progressMessage, setProgressMessage] = useState('');
  const [uploadError, setUploadError] = useState(null);  // 🆕 에러 상태 추가
  const [inlineReviewData, setInlineReviewData] = useState(restoredUpload);
  const [inlineStudents, setInlineStudents] = useState(() => (restoredUpload?.analysisResult?.students || []));
  // 파일을 올린 뒤 종류 탭을 바꿨을 때의 안내 ({from, to, count}). 새 분석/파일 바꾸기로 학생 자료가 바뀌면 사라진다.
  const [categorySwitchNotice, setCategorySwitchNotice] = useState(null);
  useEffect(() => {
    setCategorySwitchNotice(null);
  }, [inlineReviewData]);
  // 학급 관리에서 영역을 골라 가져온 학생 글이면 그 영역(메모를 고른 기준).
  const classCategory = inlineReviewData?.classCategory || '';
  const [pasteText, setPasteText] = useState('');
  const [pasteRows, setPasteRows] = useState(() => createEmptyPasteRows());
  const [pasteError, setPasteError] = useState(null);
  const [isAnalyzingPaste, setIsAnalyzingPaste] = useState(false);
  const [showTemplateGuide, setShowTemplateGuide] = useState(false);
  const [showAssistModal, setShowAssistModal] = useState(false);
  const [showPastePanel, setShowPastePanel] = useState(false);
  const [referenceFile, setReferenceFile] = useState(initialPersisted.referenceFile);
  const [referenceError, setReferenceError] = useState(null);
  const [referenceReview, setReferenceReview] = useState(null);
  // 평가가 2개 이상인 평가계획서: { items, names, analyzedData, context } — 반 전체에 쓸 평가를 먼저 고른다.
  const [referencePlan, setReferencePlan] = useState(null);
  const [isAnalyzingReference, setIsAnalyzingReference] = useState(false);
  const [teacherInstruction, setTeacherInstruction] = useState(initialPersisted.teacherInstruction);
  
  // 🔍 파일 사전 검사 상태
  const [showPreCheckModal, setShowPreCheckModal] = useState(false);
  const [preCheckResult, setPreCheckResult] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [preCheckLoading, setPreCheckLoading] = useState(false);
  const restoredBatchAppliedRef = useRef(null);
  const [batchRestoreState, setBatchRestoreState] = useState(initialBatchState || null);
  
  // 🔧 함수 참조를 위한 ref (useCallback 순서 독립성 확보)
  const actualFileUploadRef = useRef();
  
  // 옵션 상태 추가
  const [options, setOptions] = useState(initialPersisted.options);
  const [isAchievementPickerOpen, setIsAchievementPickerOpen] = useState(false);
  const [isScoringImportOpen, setIsScoringImportOpen] = useState(false);
  const [hasScoringRecords, setHasScoringRecords] = useState(false);
  const [achievementRestoreNotice, setAchievementRestoreNotice] = useState('');
  // 🎨 고른 생기부 종류를 상위로 알린다 (내 스타일 "적용 중" 표시는 공통 + 이 종류의 프로필만 본다).
  // 콜백 참조가 바뀌어도 종류가 그대로면 다시 부르지 않도록 ref 로 잡는다.
  const onCategoryChangeRef = useRef(onCategoryChange);
  onCategoryChangeRef.current = onCategoryChange;
  useEffect(() => {
    onCategoryChangeRef.current?.(options.category);
  }, [options.category]);
  // 일괄 생성에는 학교급 입력이 따로 없다(options·백엔드 배치 요청 모두 school_level 없음). 그래서 선택기의
  // 학교급 세그먼트가 이 화면의 유일한 학교급 상태다: 교과세특은 여기서 고르고(기본 중등), 세특(고)은 고등 고정.
  // options에 넣지 않아 생성 요청 본문은 이전과 같다.
  const [pickerSchoolLevel, setPickerSchoolLevel] = useState(initialPersisted.pickerSchoolLevel);
  // 설정 스냅샷을 상위로 올린다. 결과 단계에서 "다음 학급 만들기"/"입력으로 돌아가기"로 Step1 이 다시 마운트될 때
  // 같은 종류·바이트·지시·참고자료·성취기준으로 이어지게 하기 위함이다(프로덕션 분석: 후속 생성의 91%가 같은 종류).
  const onOptionsPersistRef = useRef(onOptionsPersist);
  onOptionsPersistRef.current = onOptionsPersist;
  useEffect(() => {
    onOptionsPersistRef.current?.({
      options,
      teacherInstruction,
      referenceFile,
      pickerSchoolLevel,
    });
  }, [options, teacherInstruction, referenceFile, pickerSchoolLevel]);
  const achievementPickerSchoolLevel = options.category === '교과_고등' ? '고등' : pickerSchoolLevel;

  // 선택이 없으면 키 자체를 빼서, 성취기준을 쓰지 않는 요청의 options는 이전과 동일하게 유지한다.
  const toOutgoingOptions = (current) => {
    const { achievementStandards, ...rest } = current;
    const refs = toAchievementStandardRefs(achievementStandards) || [];
    return refs.length > 0 ? { ...rest, achievementStandards: refs } : rest;
  };

  // 카테고리별 최대 바이트 (단일 생성과 일치, 2026 기재요령 반영)
  // 목표 바이트 상한 (백엔드 get_saenggibu_target_max_bytes 미러: 행발·동아리·진로 2100, 초등 창체·동아리 3000, 그 외 1500).
  // 일괄은 학교급 입력이 없어 중등 기준.
  const getMaxBytes = (category) => {
    return getSaenggibuMaxBytes(category, options.schoolLevel || '중등');
  };

  const uploadGuide = BATCH_CATEGORY_GUIDES[options.category] || BATCH_CATEGORY_GUIDES.행동발달;
  const isReferenceCategory = isBatchReferenceCategory(options.category);
  // 매 렌더마다 새 객체가 되면 이 값을 의존성으로 쓰는 useCallback 이 전부 무효화된다.
  const referenceCopy = useMemo(() => getReferenceCopy(options.category), [options.category]);
  const instructionPresets = TEACHER_INSTRUCTION_PRESETS[options.category] || TEACHER_INSTRUCTION_PRESETS.행동발달;

  const createUploadCancelledError = useCallback(() => {
    const error = new Error('분석을 중단했어요.');
    error.isUploadCancelled = true;
    return error;
  }, []);

  const throwIfUploadCancelled = useCallback(() => {
    if (uploadCancelRequestedRef.current) {
      throw createUploadCancelledError();
    }
  }, [createUploadCancelledError]);

  const stopUploadProgress = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (initialBatchState) setBatchRestoreState(initialBatchState);
  }, [initialBatchState]);

  useEffect(() => {
    if (!batchRestoreState || restoredBatchAppliedRef.current === batchRestoreState) return;
    const students = Array.isArray(batchRestoreState.students) ? batchRestoreState.students : [];
    if (students.length === 0) return;

    restoredBatchAppliedRef.current = batchRestoreState;
    const restoredOptions = {
      category: batchRestoreState.category || '행동발달',
      wordCount: batchRestoreState.wordCount || 1500,
      teacherInstruction: batchRestoreState.teacherInstruction || '',
      referenceText: batchRestoreState.referenceText || '',
      referenceFiles: [],
      achievementStandards: [],
    };
    const fromScoring = batchRestoreState.source === 'scoring';
    const fromClass = batchRestoreState.source === CLASS_RECORDS_SOURCE;
    const sourceFileName = batchRestoreState.sourceFileName || (fromScoring ? '서술형 채점 결과' : '이전 다중생성 기록');
    const restoredMetadata = {
      source_file_meta: {
        source: fromScoring || fromClass ? batchRestoreState.source : 'history',
        filename: sourceFileName,
        has_filename: Boolean(sourceFileName),
        ...(batchRestoreState.sourceFileMeta || {})
      },
      analysis_quality: {},
      structure_analysis: batchRestoreState.structureAnalysis || {
        total_students: students.length,
        detected_columns: []
      }
    };
    // 생성 화면에서 채점 결과를 불러올 때는 이미 올린 평가계획서·성취기준을 그대로 둔다.
    const keepReferences = Boolean(batchRestoreState.keepReferences);
    setOptions(prev => (keepReferences
      ? {
        ...prev,
        ...restoredOptions,
        referenceText: prev.referenceText || '',
        referenceFiles: prev.referenceFiles || [],
        achievementStandards: prev.achievementStandards || [],
      }
      : restoredOptions));
    setTeacherInstruction(restoredOptions.teacherInstruction);
    if (!keepReferences) {
      setReferenceFile(batchRestoreState.referenceText ? { name: getReferenceCopy(batchRestoreState.category).restoreName, size: 0 } : null);
    }
    setUploadedFile({
      fileName: sourceFileName,
      fileSize: batchRestoreState.sourceFileSize || 0,
      uploadTime: new Date().toISOString(),
      fromHistory: true
    });
    setInlineReviewData({
      fileName: sourceFileName,
      fileSize: batchRestoreState.sourceFileSize || 0,
      uploadTime: new Date().toISOString(),
      analysisResult: {
        structure_analysis: restoredMetadata.structure_analysis,
        students,
        overall_quality: {
          average_confidence: 1,
          total_processable: students.length,
          recommendations: [
            fromScoring
              ? '서술형 채점 결과에서 가져온 학생 정보입니다. 점수·등급은 넘기지 않았어요.'
              : fromClass
                ? '학급 관리에서 가져온 메모·채점 기록입니다. 학생마다 내용을 확인하고, 넣고 싶지 않은 줄은 지워 주세요.'
                : batchRestoreState.source === 'single_file'
                ? '단일 생성에 올린 파일에서 읽은 학생 정보입니다. 이름과 내용을 확인해주세요.'
                : '기록에서 복원한 학생 정보입니다.',
          ]
        }
      },
      fileMeta: restoredMetadata.source_file_meta,
      metadata: restoredMetadata,
      options: restoredOptions,
      ...(fromClass ? { classCategory: restoredOptions.category, classTargetBytes: restoredOptions.wordCount } : {}),
    });
    setInlineStudents(students);
    setAchievementRestoreNotice('');

    const historyRefs = refsFromHistoryRecord(batchRestoreState);
    if (historyRefs.length === 0) return undefined;
    let cancelled = false;
    restoreValidatedStandards(historyRefs).then(({ droppedCount, records }) => {
      if (cancelled) return;
      setOptions((prev) => ({ ...prev, achievementStandards: records }));
      setInlineReviewData((prev) => (
        prev
          ? { ...prev, options: { ...prev.options, achievementStandards: records } }
          : prev
      ));
      setAchievementRestoreNotice(droppedStandardsNotice(droppedCount));
    });
    return () => {
      cancelled = true;
    };
  }, [batchRestoreState]);

  // 채점 기록이 있는 교과 선생님에게만 '채점 결과 불러오기'를 보여 준다.
  const isScoringImportCategory = options.category === '교과' || options.category === '교과_고등';
  const scoringRecordsCheckedRef = useRef('');
  useEffect(() => {
    const uid = currentUser?.uid || '';
    if (!uid || !isScoringImportCategory || scoringRecordsCheckedRef.current === uid) return undefined;
    scoringRecordsCheckedRef.current = uid;
    Promise.resolve()
      .then(() => getScoringHistory(1, 0, isProUser))
      .then(response => setHasScoringRecords((response?.records || []).length > 0))
      .catch(() => {});
    return undefined;
  }, [currentUser, isProUser, isScoringImportCategory]);

  const handleScoringImport = useCallback((batchRestore) => {
    setIsScoringImportOpen(false);
    const ownLines = String(teacherInstruction || '')
      .split('\n')
      .filter(line => line.trim() && !line.startsWith('아래 학생별 내용은'));
    const instruction = [...ownLines, batchRestore.teacherInstruction].filter(Boolean).join('\n');
    setBatchRestoreState({
      ...batchRestore,
      category: isScoringImportCategory ? options.category : batchRestore.category,
      wordCount: options.wordCount || batchRestore.wordCount,
      teacherInstruction: instruction,
      keepReferences: true,
    });
  }, [isScoringImportCategory, options.category, options.wordCount, teacherInstruction]);

  // 카테고리 변경 시 wordCount가 max를 초과하면 자동 클램프
  useEffect(() => {
    const maxBytes = getSaenggibuMaxBytes(options.category, options.schoolLevel || '중등');
    if (options.wordCount > maxBytes) {
      setOptions(prev => ({ ...prev, wordCount: maxBytes }));
    }
  }, [options.category, options.schoolLevel, options.wordCount]);

  // 파일 유효성 검사 - 모든 형식 지원.
  // 원본은 Storage descriptor(개당 50MB)로 업로드되므로 Cloud Run 30MB 제한을 받지 않는다.
  const validateFile = useCallback((file) => {
    const maxSize = BATCH_STORAGE_FILE_LIMIT_BYTES;
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    
    if (isClearlyBlockedUploadFile(file)) {
      throw new Error('코드/문서/이미지 파일만 올릴 수 있어요.');
    }

    if (file.size > maxSize) {
      throw new Error(`파일 크기는 ${maxSizeMB}MB 이하여야 해요.`);
    }

    return true;
  }, []);

  const getAnalysisWarnings = useCallback((result, analyzedData) => {
    const warnings = [];
    if (result?.warning) warnings.push(result.warning);
    if (Array.isArray(result?.warnings)) warnings.push(...result.warnings);
    if (Array.isArray(analyzedData?.analysis_warnings)) warnings.push(...analyzedData.analysis_warnings);
    if (Array.isArray(analyzedData?.warnings)) warnings.push(...analyzedData.warnings);
    if (result?.was_truncated) warnings.push('파일 내용이 길어 일부만 분석했어요.');
    return [...new Set(warnings.filter(Boolean))];
  }, []);

  const buildSubjectReferenceDraft = useCallback((data = {}) => {
    const buildOne = (item = {}, index = null) => {
      const lines = [];
      if (index !== null) lines.push(`[평가 단위 ${index + 1}]`);
      addReferenceDraftSection(lines, '과목', item.subject);
      addReferenceDraftSection(lines, '단원', item.unit);
      addReferenceDraftSection(lines, '영역', item.domain);
      addReferenceDraftSection(lines, '활동명', pickReferenceAnalysisText(item, ['activity_name', 'name']));
      addReferenceDraftSection(lines, '성취기준', pickReferenceAnalysisText(item, ['achievement_standard', 'achievement_standards', 'achievement']));
      addReferenceDraftSection(lines, '평가방법', pickReferenceAnalysisText(item, ['evaluation_method', 'method', 'assessment_method']));
      addReferenceDraftSection(lines, '평가요소', pickReferenceAnalysisText(item, ['evaluation_items', 'evaluation_elements', 'assessment_elements', 'evaluation_item']));
      addReferenceDraftSection(lines, '평가기준', pickReferenceAnalysisText(item, ['evaluation_criteria', 'criteria', 'rubric', 'scoring_criteria', 'scoring_levels']));
      addReferenceDraftSection(lines, '평가내용', pickReferenceAnalysisText(item, ['evaluation_content', 'assessment_content', 'task', 'task_description']));
      addReferenceDraftSection(lines, '수업-평가 연계', item.class_evaluation_link);
      addReferenceDraftSection(lines, '평가시기', item.evaluation_period);
      addReferenceDraftSection(lines, '평가비율', item.evaluation_ratio);
      return lines.join('\n\n').trim();
    };

    const items = data.parsed_activities || data.parsed_subjects || [];
    if (Array.isArray(items) && items.length > 0) {
      return items.map((item, index) => buildOne(item, index)).filter(Boolean).join('\n\n');
    }
    return buildOne(data);
  }, []);


  const isLikelyEvaluationPlanFile = useCallback((file) => {
    if (!isReferenceCategory || !file?.name) return false;
    const normalizedName = file.name.replace(/\s+/g, '').toLowerCase();
    const planKeywords = options.category === '동아리'
      ? ['동아리계획', '연간계획', '운영계획', '활동계획']
      : ['평가계획', '평가운영', '채점기준', '루브릭', '성취기준', '논술형평가', '서술형평가', '수행평가계획'];
    const studentRecordKeywords = ['학생', '명단', '기록', '감상문', '보고서', '활동지', '발표기록', '결과'];
    return planKeywords.some(keyword => normalizedName.includes(keyword)) &&
      !studentRecordKeywords.some(keyword => normalizedName.includes(keyword));
  }, [isReferenceCategory, options.category]);

  // data: 분석 결과 전체(합쳐서 쓰기) 또는 고른 평가 하나.
  const openReferenceDraftReview = useCallback((data, { fileCount, fileMeta, payloads, warnings, fallbackText, planPick = null }) => {
    const draft = (
      options.category === '동아리'
        ? buildClubReferenceDraft(data)
        : buildSubjectReferenceDraft(data)
    ) || fallbackText || '';
    if (!draft.trim()) {
      throw new Error(referenceCopy.emptyError);
    }

    setReferenceReview({
      title: referenceCopy.reviewTitle(fileCount),
      description: '이 내용은 학생별 기록을 해석하는 기준으로만 사용돼요. 맞게 읽었는지 확인해주세요.',
      text: draft,
      rawText: draft,
      rawSummaryLabel: '참고자료로 들어갈 내용 보기',
      confidence: data.confidence,
      warnings,
      categoryLabel: referenceCopy.categoryLabel,
      editLabel: referenceCopy.editLabel,
      confirmLabel: '참고자료로 넣기',
      resetLabel: '다시 선택',
      fileMeta,
      payloads,
      planPick,
      summaryItems: buildReferenceSummaryItems(data, options.category),
      editFields: [
        { key: 'text', label: referenceCopy.fieldLabel, value: draft, rows: 10 }
      ]
    });
  }, [buildSubjectReferenceDraft, options.category, referenceCopy]);

  // 업로드/자료함 공용: 분석 응답 → 참고자료 확인 모달. payloads 는 기록 저장 시 첨부 메타(이름 해시)가 된다.
  const openReferenceReviewFromResult = useCallback((result, { fileCount, fileMeta, payloads }) => {
    const analyzedData = result.analyzed_data || {};
    // Phase 4: 서버가 영속화한 계획서 원본 id 를 descriptor 에 붙여 기록 저장 시 첨부 메타(이름 해시)로 남긴다.
    const infos = Array.isArray(result.file_infos) ? result.file_infos : [];
    infos.forEach((info, index) => {
      if (!payloads[index] || !info) return;
      if (info.plan_file_id) payloads[index].plan_file_id = info.plan_file_id;
      if (info.reuse_match) payloads[index].reuse_match = true;
    });
    const context = {
      fileCount,
      fileMeta,
      payloads,
      warnings: getAnalysisWarnings(result, analyzedData),
      fallbackText: result.extracted_text_preview || '',
    };
    const planItems = options.category === '동아리' ? [] : listPlanItems(analyzedData);
    if (shouldPickBatchPlanItem(planItems)) {
      setReferenceReview(null);
      setReferencePlan({
        items: planItems,
        names: buildPlanItemNames(planItems, planItemSuffix),
        analyzedData,
        context,
      });
      trackSaenggibuUxEvent('saenggibu_batch_eval_plan_pick', {
        category: options.category, is_batch: true, stage: 'shown', item_count: planItems.length,
      });
      return;
    }
    setReferencePlan(null);
    openReferenceDraftReview(analyzedData, context);
  }, [getAnalysisWarnings, openReferenceDraftReview, options.category]);

  // index: 고른 평가 순서, 'all': 모두 합쳐서 쓰기
  const handlePickReferencePlanItem = useCallback((index) => {
    if (!referencePlan) return;
    const isAll = index === 'all';
    const data = isAll ? referencePlan.analyzedData : referencePlan.items[index];
    const planLabel = isAll ? `평가 ${referencePlan.items.length}개 모두` : referencePlan.names[index];
    try {
      setReferenceError(null);
      openReferenceDraftReview(data, {
        ...referencePlan.context,
        fileMeta: { ...referencePlan.context.fileMeta, planLabel },
        planPick: buildPlanSelectionMeta(isAll ? 'merge' : 'single', referencePlan.items.length),
      });
      trackSaenggibuUxEvent('saenggibu_batch_eval_plan_pick', {
        category: options.category,
        is_batch: true,
        stage: isAll ? 'all' : 'single',
        item_count: referencePlan.items.length,
      });
    } catch (error) {
      setReferenceError(error.message || referenceCopy.addError);
    }
  }, [openReferenceDraftReview, options.category, referenceCopy.addError, referencePlan]);

  const handleChangeReferencePlanItem = useCallback(() => {
    setReferenceFile(null);
    setReferenceError(null);
    setOptions(prev => ({ ...withoutPlanPick(prev), referenceText: '', referenceFiles: [] }));
    trackSaenggibuUxEvent('saenggibu_batch_eval_plan_pick', {
      category: options.category,
      is_batch: true,
      stage: 'change',
      item_count: referencePlan?.items?.length || 0,
    });
  }, [options.category, referencePlan]);

  const addReferenceFile = useCallback(async (fileInput) => {
    const files = Array.isArray(fileInput) ? fileInput : Array.from(fileInput ? [fileInput] : []);
    if (files.length === 0) return;

    try {
      if (!currentUser) {
        throw new Error('로그인이 필요해요.');
      }
      if (files.length > BATCH_REFERENCE_MAX_FILES) {
        throw new Error(referenceCopy.limitError(BATCH_REFERENCE_MAX_FILES));
      }
      const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);
      if (totalSize > BATCH_REFERENCE_TOTAL_LIMIT_BYTES) {
        throw new Error(referenceCopy.sizeError);
      }

      files.forEach(validateFile);
      setIsAnalyzingReference(true);
      setReferenceError(null);
      const payloads = await Promise.all(
        files.map(file => uploadSaenggibuAttachment(file, currentUser.uid))
      );

      const formData = new FormData();
      formData.append('file_descriptors', JSON.stringify(payloads));
      formData.append('category', referenceCopy.analysisCategory);
      const token = await currentUser.getIdToken();
      const response = await fetch(getApiUrl('/api/saenggibu/file/upload'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        const detail = Array.isArray(result?.detail)
          ? result.detail.map(item => item?.msg || JSON.stringify(item)).join('\n')
          : result?.detail;
        throw new Error(detail || result?.message || referenceCopy.analyzeError);
      }

      const fileMeta = files.length === 1
        ? { name: files[0].name, size: files[0].size, files: files.map(file => ({ name: file.name, size: file.size })) }
        : { name: `${files.length}개 ${referenceCopy.shortLabel}`, size: totalSize, files: files.map(file => ({ name: file.name, size: file.size })) };
      openReferenceReviewFromResult(result, { fileCount: files.length, fileMeta, payloads });
    } catch (error) {
      setReferenceError(error.message || referenceCopy.addError);
    } finally {
      setIsAnalyzingReference(false);
    }
  }, [currentUser, openReferenceReviewFromResult, referenceCopy, validateFile]);

  // 📚 자료함에서 고르기 (Phase 4b) — Storage 업로드 없이 /file/from-library 로 분석. 확인 모달·상태는 업로드와 같다.
  const addReferenceFromLibrary = useCallback(async (material) => {
    if (!material?.id) return;
    try {
      if (!currentUser) {
        throw new Error('로그인이 필요해요.');
      }
      setIsAnalyzingReference(true);
      setReferenceError(null);
      const result = await analyzeSaenggibuFileFromLibrary({
        materialId: material.id,
        category: referenceCopy.analysisCategory,
      });
      if (!result?.success) {
        throw new Error(result?.detail || result?.message || referenceCopy.analyzeError);
      }
      const descriptor = buildLibraryAttachmentDescriptor(material);
      const fileMeta = {
        name: descriptor.name,
        size: descriptor.size,
        source: LIBRARY_SOURCE,
        files: [{ name: descriptor.name, size: descriptor.size, source: LIBRARY_SOURCE }],
      };
      openReferenceReviewFromResult(result, { fileCount: 1, fileMeta, payloads: [descriptor] });
    } catch (error) {
      setReferenceError(error.message || referenceCopy.addError);
    } finally {
      setIsAnalyzingReference(false);
    }
  }, [currentUser, openReferenceReviewFromResult, referenceCopy]);


  const handleConfirmReferenceReview = useCallback((review) => {
    const payloads = review.payloads || (review.payload ? [review.payload] : []);
    const referenceText = (review.fieldValues?.text ?? review.text ?? '').trim();
    setReferenceFile(review.fileMeta || null);
    setReferenceError(null);
    setReferenceReview(null);
    setOptions(prev => ({
      ...withoutPlanPick(prev),
      referenceText,
      referenceFiles: payloads,
      ...(review.planPick?.eval_plan_mode ? {
        evalPlanMode: review.planPick.eval_plan_mode,
        evalUnitDetectedCount: review.planPick.eval_unit_detected_count,
      } : {}),
    }));
    showToast(referenceCopy.toast, 'success', 6000);
  }, [referenceCopy.toast, showToast]);

  const handleReferenceFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    await addReferenceFile(files);
  }, [addReferenceFile]);

  const handleReferenceDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReferenceDragOver(true);
  }, []);

  const handleReferenceDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReferenceDragOver(false);
  }, []);

  const handleReferenceDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReferenceDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    addReferenceFile(files);
  }, [addReferenceFile]);

  const handleRemoveReferenceFile = useCallback(() => {
    setReferenceFile(null);
    setReferenceError(null);
    setReferenceReview(null);
    setReferencePlan(null);
    setOptions(prev => ({
      ...withoutPlanPick(prev),
      referenceText: '',
      referenceFiles: []
    }));
  }, []);

  const handleReferenceTextChange = useCallback((value) => {
    const shouldClearFile = Boolean(value && referenceFile);
    if (shouldClearFile) {
      setReferenceFile(null);
      setReferencePlan(null);
    }
    setOptions(prev => ({
      ...(shouldClearFile ? withoutPlanPick(prev) : prev),
      referenceText: value,
      referenceFiles: shouldClearFile ? [] : prev.referenceFiles
    }));
  }, [referenceFile]);

  const handleTeacherInstructionChange = useCallback((value) => {
    setTeacherInstruction(value);
    setOptions(prev => ({
      ...prev,
      teacherInstruction: value
    }));
  }, []);

  const handleInstructionPreset = useCallback((preset) => {
    const presetInstruction = typeof preset === 'string' ? preset : preset.instruction;
    const nextInstruction = teacherInstruction
      ? `${teacherInstruction.trim()}\n${presetInstruction}`
      : presetInstruction;
    handleTeacherInstructionChange(nextInstruction);
  }, [handleTeacherInstructionChange, teacherInstruction]);

  // 🔍 파일 사전 검사 수행
  const performFilePreCheck = useCallback(async (fileOrFiles) => {
    const files = (Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]).filter(Boolean);
    if (files.length === 0) return;
    const file = files[0];
    const MAX_REFERENCE_FILE_SIZE = 20 * 1024 * 1024;
    if (files.length === 1 && file.size > MAX_REFERENCE_FILE_SIZE) {
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        title: '파일 용량을 줄여주세요',
        message: `참고 파일이 ${(file.size / 1024 / 1024).toFixed(1)}MB예요. 한 번에 20MB까지만 첨부할 수 있어요.`,
        total_files: 1,
        total_size: file.size,
        total_size_display: `${(file.size / 1024 / 1024).toFixed(1)}MB`,
        files_info: [{
          filename: file.name,
          status: 'warning',
          file_size: file.size,
          file_size_display: `${(file.size / 1024 / 1024).toFixed(1)}MB`,
          issues: [],
        }],
        recommendations: ['파일을 압축하거나 나눠서 첨부해 주세요.'],
      });
      setPendingFile(null);
      setPreCheckLoading(false);
      setShowPreCheckModal(true);
      return;
    }
    if (files.length > 40) {
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        title: '파일 개수를 줄여주세요',
        message: '한 번에 최대 40개 파일까지 올릴 수 있어요.',
        total_files: files.length,
        total_size: files.reduce((sum, item) => sum + item.size, 0),
        total_size_display: `${(files.reduce((sum, item) => sum + item.size, 0) / 1024 / 1024).toFixed(1)}MB`,
        files_info: files.map(item => ({
          filename: item.name,
          status: 'warning',
          file_size: item.size,
          file_size_display: `${(item.size / 1024 / 1024).toFixed(1)}MB`,
          issues: [],
        })),
        recommendations: ['파일을 40개 이하로 나눠서 올려주세요.'],
      });
      setPendingFile(null);
      setPreCheckLoading(false);
      setShowPreCheckModal(true);
      return;
    }

    if (files.length > 1) {
      try {
        files.forEach(validateFile);
      } catch (error) {
        setPreCheckResult({
          can_proceed: false,
          needs_confirmation: false,
          overall_status: 'error',
          title: '파일을 확인해주세요',
          message: error.message || '올릴 수 없는 파일이 포함되어 있어요.',
          total_files: files.length,
          total_size: files.reduce((sum, item) => sum + item.size, 0),
          total_size_display: `${(files.reduce((sum, item) => sum + item.size, 0) / 1024 / 1024).toFixed(1)}MB`,
          files_info: files.map(item => ({
            filename: item.name,
            status: 'warning',
            file_size: item.size,
            file_size_display: `${(item.size / 1024 / 1024).toFixed(1)}MB`,
            issues: [],
          })),
          recommendations: ['지원 파일 형식과 파일 크기를 확인해주세요.'],
        });
        setPendingFile(null);
        setPreCheckLoading(false);
        setShowPreCheckModal(true);
        return;
      }
      actualFileUploadRef.current(files);
      return;
    }

    // 로딩 시작
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    try {
      const result = await preCheckFiles(files, 'batch', isProUser);
      
      // 검사 통과 & 확인 불필요 시 모달 없이 바로 업로드 진행
      if (result.can_proceed && !result.needs_confirmation) {
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        actualFileUploadRef.current(files.length === 1 ? files[0] : files);  // 🔧 ref를 통해 최신 함수 호출
        return;
      }
      
      // 확인이 필요하거나 진행 불가인 경우에만 모달 표시
      setPreCheckResult(result);
      setPendingFile(files.length === 1 ? files[0] : files);
      setPreCheckLoading(false);
      
    } catch (error) {
      console.error('파일 사전 검사 오류:', error);
      // 오류 시에도 업로드 진행 가능
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      actualFileUploadRef.current(files.length === 1 ? files[0] : files);  // 🔧 ref를 통해 최신 함수 호출
    }
  }, [isProUser, validateFile]);  // isProUser 는 사전검사 한도 표기용

  // 🔍 사전 검사 확인 후 업로드 진행
  const handlePreCheckConfirm = useCallback(() => {
    setShowPreCheckModal(false);
    if (pendingFile) {
      actualFileUploadRef.current(pendingFile);  // 🔧 ref를 통해 최신 함수 호출
    }
    setPendingFile(null);
    setPreCheckResult(null);
  }, [pendingFile]);  // 🔧 업로드 함수 의존성 제거 (ref 사용)

  // 🔍 사전 검사 취소
  const handlePreCheckCancel = useCallback(() => {
    setShowPreCheckModal(false);
    setPendingFile(null);
    setPreCheckResult(null);
  }, []);

  const handleCancelFileAnalysis = useCallback(() => {
    if (!isUploading) return;
    uploadCancelRequestedRef.current = true;
    setProgressMessage('분석을 중단하고 있어요.');
    uploadAbortControllerRef.current?.abort();
  }, [isUploading]);

  useEffect(() => () => {
    uploadCancelRequestedRef.current = true;
    uploadAbortControllerRef.current?.abort();
    stopUploadProgress();
  }, [stopUploadProgress]);

  // 파일 처리 함수 (업로드 → 분석 → 확인 단계로 이동)
  const actualFileUpload = useCallback(async (fileOrFiles) => {
    // 🆕 중복 호출 방지
    if (isUploading) {
      return;
    }
    const filesToUpload = (Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]).filter(Boolean);
    if (filesToUpload.length === 0) return;
    
    uploadCancelRequestedRef.current = false;
    const abortController = new AbortController();
    uploadAbortControllerRef.current = abortController;

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setRemainingTime(120);
      setProgressMessage('파일 검증 중...');
      setUploadError(null);  // 이전 에러 초기화

      if (filesToUpload.length > 40) {
        throw new Error('한 번에 최대 40개 파일까지 올릴 수 있어요.');
      }

      if (filesToUpload.length > 1 && !BATCH_MULTI_FILE_CATEGORIES.includes(options.category)) {
        const supported = BATCH_MULTI_FILE_CATEGORIES.map(getBatchCategoryLabel).join(', ');
        throw new Error(`여러 파일 업로드는 ${supported}에서 지원해요.`);
      }

      // 유효성 검사
      filesToUpload.forEach(validateFile);
      throwIfUploadCancelled();

      const isMultiFileUpload = filesToUpload.length > 1;
      const totalFileSize = filesToUpload.reduce((sum, item) => sum + item.size, 0);
      const firstFile = filesToUpload[0];

      const fileData = {
        fileName: isMultiFileUpload ? `${filesToUpload.length}개 파일` : firstFile.name,
        fileSize: totalFileSize,
        uploadTime: new Date().toISOString(),
        originalFile: firstFile,
        originalFiles: filesToUpload
      };

      setUploadedFile(fileData);
      
      const estimatedSeconds = isMultiFileUpload
        ? Math.min(Math.max(filesToUpload.length * 45, 90), 600)
        : 60;
      // 🆕 프로그레스바: 큰 파일/여러 파일 대응
      const progressDuration = Math.max(estimatedSeconds, 120) * 1000;
      const displayDuration = estimatedSeconds;
      const startTime = Date.now();
      
      // 프로그레스 인터벌 시작
      progressIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const newProgress = Math.min(Math.round((elapsed / progressDuration) * 95), 95);
        setUploadProgress(newProgress);
        
        // 남은 시간 계산 (표시용 2분 기준)
        const elapsedSeconds = Math.floor(elapsed / 1000);
        const remaining = Math.max(displayDuration - elapsedSeconds, 0);
        setRemainingTime(remaining);
        
        // 🆕 분:초 형식으로 표시
        const minutes = Math.floor(remaining / 60);
        const seconds = remaining % 60;
        if (remaining > 0) {
          const timeStr = minutes > 0 ? `${minutes}분 ${seconds}초` : `${seconds}초`;
          setProgressMessage(`학생 정보 분석 중... 약 ${timeStr} 남음`);
        } else {
          setProgressMessage('학생 정보 분석 중... 거의 완료');
        }
      }, 500);
      
      // 인증 토큰 가져오기
      if (!currentUser) {
        throw new Error('로그인이 필요해요.');
      }
      throwIfUploadCancelled();
      
      // 백엔드로 분석 요청
      let analysisResult;
      if (isMultiFileUpload) {
        const combinedStudents = [];
        const sourceFileMetas = [];
        const failedFileMetas = [];
        const combinedWarnings = [];
        const fileResults = new Array(filesToUpload.length);
        let nextFileIndex = 0;
        let completedFileCount = 0;
        const workerCount = Math.min(2, filesToUpload.length);

        const analyzeSingleFile = async (fileIndex) => {
          throwIfUploadCancelled();
          const currentFile = filesToUpload[fileIndex];
          setProgressMessage(`${fileIndex + 1}/${filesToUpload.length}번째 파일을 읽고 있어요.`);
          const authToken = await currentUser.getIdToken();
          const descriptor = await uploadSaenggibuAttachment(currentFile, currentUser.uid);
          const singleResult = await analyzeExcelFile(descriptor, authToken, options.category, {
            signal: abortController.signal
          });
          throwIfUploadCancelled();
          return {
            index: fileIndex,
            file: currentFile,
            result: singleResult,
            students: singleResult.data?.students || [],
            warnings: Array.isArray(singleResult.warnings) ? singleResult.warnings : [],
            fileMeta: {
              ...(singleResult.file_meta || {}),
              filename: currentFile.name,
              file_size: currentFile.size
            }
          };
        };

        await Promise.all(
          Array.from({ length: workerCount }, async () => {
            while (nextFileIndex < filesToUpload.length) {
              const fileIndex = nextFileIndex;
              nextFileIndex += 1;
              try {
                fileResults[fileIndex] = await analyzeSingleFile(fileIndex);
              } catch (fileError) {
                if (uploadCancelRequestedRef.current || fileError.isUploadCancelled) {
                  throw fileError;
                }
                const currentFile = filesToUpload[fileIndex];
                const message = fileError.response?.data?.detail || fileError.message || '분석 실패';
                fileResults[fileIndex] = {
                  index: fileIndex,
                  file: currentFile,
                  error: message
                };
              } finally {
                completedFileCount += 1;
                setUploadProgress(Math.min(95, Math.round((completedFileCount / filesToUpload.length) * 90)));
                setProgressMessage(`${completedFileCount}/${filesToUpload.length}개 파일 분석 완료`);
              }
            }
          })
        );

        fileResults.forEach((fileResult, fileIndex) => {
          if (!fileResult) return;
          if (fileResult.error) {
            failedFileMetas.push({
              filename: fileResult.file.name,
              file_size: fileResult.file.size,
              error: fileResult.error
            });
            combinedWarnings.push(`${fileResult.file.name} 분석 실패: ${fileResult.error}`);
            return;
          }

          const currentFile = fileResult.file;
          const singleStudents = fileResult.students;
          sourceFileMetas.push(fileResult.fileMeta);
          combinedWarnings.push(...fileResult.warnings);

          singleStudents.forEach((student, studentIndex) => {
            const nextName = resolveMultiFileStudentName({
              studentName: student.name,
              filename: currentFile.name,
              fileMeta: fileResult.fileMeta,
              fallbackIndex: combinedStudents.length + 1,
            });
            combinedStudents.push({
              ...student,
              id: `file_${fileIndex + 1}_${student.id || studentIndex + 1}`,
              name: nextName,
              characteristics: '',
              source_file_name: currentFile.name,
              warnings: [
                ...(student.warnings || []),
                ...(singleStudents.length > 1 ? [`${currentFile.name}에서 ${singleStudents.length}명으로 나뉘었어요.`] : [])
              ]
            });
          });
        });

        if (combinedStudents.length === 0) {
          throw new Error('모든 파일에서 학생 정보를 찾지 못했어요. 파일에 학생 이름과 제출물 내용이 있는지 확인해주세요.');
        }

        const averageConfidence = combinedStudents.length > 0
          ? Math.round((combinedStudents.reduce((sum, student) => sum + (Number(student.confidence) || 0.8), 0) / combinedStudents.length) * 100) / 100
          : 0;
        analysisResult = {
          success: true,
          data: {
            structure_analysis: {
              total_students: combinedStudents.length,
              detected_columns: ['파일명', '원본 제출물'],
              analysis_method: '학생별 파일 원본 분석'
            },
            students: combinedStudents,
            overall_quality: {
              average_confidence: averageConfidence,
              total_processable: combinedStudents.length,
              recommendations: [
                `총 ${combinedStudents.length}명의 학생 정보가 감지되었습니다.`,
                ...(failedFileMetas.length > 0 ? [`${failedFileMetas.length}개 파일은 분석하지 못했어요.`] : []),
                '생성 전에 학생별 원본 내용을 확인해주세요.'
              ]
            }
          },
          file_meta: {
            source: 'multi_file',
            filename: `${filesToUpload.length}개 파일`,
            ext: 'multiple',
            mime_type: '',
            file_size: totalFileSize,
            detected_students: combinedStudents.length,
            analysis_method: '학생별 파일 원본 분석',
            files: sourceFileMetas,
            errors: failedFileMetas
          },
          warnings: [...new Set(combinedWarnings)]
        };
      } else {
        const authToken = await currentUser.getIdToken();
        const descriptor = await uploadSaenggibuAttachment(firstFile, currentUser.uid);
        analysisResult = await analyzeExcelFile(descriptor, authToken, options.category, {
          signal: abortController.signal
        });
        throwIfUploadCancelled();
      }
      
      const students = analysisResult.data?.students || [];
      setProgressMessage(`${students.length}명 분석 완료. 내용을 확인해주세요.`);
      
      // 완료 시 인터벌 정리
      stopUploadProgress();

      setProgressMessage(`${students.length}명 학생 정보를 찾았어요.`);
      setUploadProgress(100);
      setRemainingTime(0);
      setIsUploading(false);

      showToast('분석한 내용을 확인해주세요.', 'success');

      const metadata = {
        source_file_meta: {
          source: 'file',
          ...(analysisResult.file_meta || {
            filename: firstFile.name,
            ext: firstFile.name.includes('.') ? firstFile.name.split('.').pop().toLowerCase() : '',
            mime_type: firstFile.type || '',
            file_size: totalFileSize
          })
        },
        analysis_quality: analysisResult.data?.overall_quality || {},
        structure_analysis: analysisResult.data?.structure_analysis || {}
      };

      setInlineReviewData({
        ...fileData,
        analysisResult: analysisResult.data,
        fileMeta: analysisResult.file_meta,
        warnings: collectAnalysisWarnings(analysisResult),
        confidence: analysisResult.confidence,
        wasTruncated: Boolean(analysisResult.was_truncated),
        metadata,
        options
      });
      setInlineStudents(students);
      uploadAbortControllerRef.current = null;
      
    } catch (error) {
      // 에러 시 인터벌 정리
      stopUploadProgress();
      uploadAbortControllerRef.current = null;
      if (uploadCancelRequestedRef.current || error.isUploadCancelled) {
        setIsUploading(false);
        setUploadProgress(0);
        setRemainingTime(0);
        setProgressMessage('');
        setUploadedFile(null);
        setUploadError(null);
        setInlineReviewData(null);
        setInlineStudents([]);
        showToast('분석을 중단했어요.', 'info');
        uploadCancelRequestedRef.current = false;
        return;
      }
      console.error('파일 처리 오류:', error);
      
      // 구체적인 에러 메시지 설정
      let errorTitle = '파일 분석 중 오류가 발생했어요';
      let errorDetail = '파일을 읽지 못했어요.';
      
      if (error.message.includes('코드/문서/이미지')) {
        errorTitle = '지원하지 않는 파일 형식';
        errorDetail = '코드, 문서, 이미지 파일만 사용할 수 있어요.';
      } else if (error.message.includes('MB 이하')) {
        errorTitle = '파일 크기 초과';
        errorDetail = error.message;
      } else if (error.message.includes('로그인')) {
        errorTitle = '로그인 필요';
        errorDetail = '로그인이 필요해요.';
      } else if (error.response) {
        // 백엔드에서 반환한 에러 메시지 사용
        const backendError = error.response.data?.detail || '';
        
        if (backendError) {
          errorTitle = 'AI가 학생 정보를 찾지 못했습니다';
          errorDetail = backendError;
        } else if (error.response.status === 400) {
          errorTitle = '학생 정보를 찾지 못했어요';
          errorDetail = '학생 이름과 내용이 있는지 확인해주세요.';
        } else if (error.response.status === 500) {
          errorTitle = 'AI 분석 실패';
          errorDetail = '잠시 후 다시 시도해주세요.';
        }
      } else if (error.message.includes('Network')) {
        errorTitle = '네트워크 오류';
        errorDetail = '인터넷 연결을 확인해주세요.';
      } else if (error.message) {
        errorDetail = error.message;
      }
      
      // 🆕 에러 상태 설정 (UI에 표시)
      setUploadError({
        title: errorTitle,
        detail: errorDetail
      });
      
      setIsUploading(false);
      setUploadProgress(0);
      setProgressMessage('');
      setUploadedFile(null);
      setInlineReviewData(null);
      setInlineStudents([]);
    }
  }, [isUploading, showToast, currentUser, options, validateFile, throwIfUploadCancelled, stopUploadProgress]);
  
  // 🔧 ref 업데이트 (최신 함수 참조 유지)
  actualFileUploadRef.current = actualFileUpload;

  // 드래그 앤 드롭 핸들러
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // PRO 체크
    if (!isProUser) {
      setShowProModal(true);
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (files.length === 1 && isLikelyEvaluationPlanFile(files[0])) {
        addReferenceFile(files[0]);
        return;
      }
      if (files.length > 1) {
        performFilePreCheck(files);
      } else {
        performFilePreCheck(files[0]);
      }
    }
  }, [addReferenceFile, isLikelyEvaluationPlanFile, performFilePreCheck, isProUser]);

  // 파일 선택 핸들러
  const handleFileSelect = useCallback((e) => {
    // PRO 체크
    if (!isProUser) {
      setShowProModal(true);
      e.target.value = ''; // input 초기화
      return;
    }

    const files = Array.from(e.target.files);
    if (files.length > 0) {
      if (files.length === 1 && isLikelyEvaluationPlanFile(files[0])) {
        addReferenceFile(files[0]);
        e.target.value = '';
        return;
      }
      if (files.length > 1) {
        performFilePreCheck(files);
      } else {
        performFilePreCheck(files[0]);
      }
    }
    e.target.value = '';
  }, [addReferenceFile, isLikelyEvaluationPlanFile, performFilePreCheck, isProUser]);

  // 파일 선택 버튼 클릭
  const handleSelectClick = (e) => {
    // 파일 input 자신의 click이 버블링되어 재진입하면 무한 파일선택 루프가 되므로 차단
    if (e?.target?.closest?.('input[type="file"]')) return;
    // PRO 체크
    if (!isProUser) {
      setShowProModal(true);
      return;
    }
    fileInputRef.current?.click();
  };

  // 올린 파일 카드의 "파일 바꾸기": 학생 자료만 비우고(설정은 유지) 파일 선택창을 연다.
  // 사용자 제스처의 동기 콜스택에서 열어야 선택창이 뜨므로 await 를 두지 않는다.
  const handleReplaceUploadedFile = (e) => {
    e?.stopPropagation?.();
    if (e?.target?.closest?.('input[type="file"]')) return;
    if (isUploading) return;
    setUploadedFile(null);
    setUploadError(null);
    setInlineReviewData(null);
    setInlineStudents([]);
    if (!isProUser) {
      setShowProModal(true);
      return;
    }
    fileInputRef.current?.click();
  };

  const getInlineStudentContent = useCallback((student) => (
    student.existing_content || student.content || ''
  ), []);

  const handleInlineStudentNameChange = useCallback((index, name) => {
    setInlineStudents(prevStudents => prevStudents.map((student, studentIndex) => (
      studentIndex === index
        ? {
          ...student,
          name,
          _recognitionEdited: true,
        }
        : student
    )));
  }, []);

  const handleInlineStudentContentChange = useCallback((index, content) => {
    setInlineStudents(prevStudents => prevStudents.map((student, studentIndex) => (
      studentIndex === index
        ? {
          ...student,
          existing_content: content,
          content,
          _recognitionEdited: true,
        }
        : student
    )));
  }, []);

  const handleInlineStudentRemove = useCallback((index) => {
    setInlineStudents(prevStudents => prevStudents.filter((_, studentIndex) => studentIndex !== index));
  }, []);

  // 스캔 PDF 학생 범위 확인 게이트 (감사 P0-2 / 권고 5): 경계 감지가 폴백(1쪽=1명)이거나 확신이 낮으면
  // 교사가 학생 수·페이지 범위를 확인했다고 표시해야 생성으로 넘어간다.
  const scanRangeReview = useMemo(() => getScanRangeReview(inlineReviewData?.fileMeta), [inlineReviewData]);
  const readGapMessages = useMemo(
    () => getReadGapMessages(inlineReviewData?.fileMeta, inlineReviewData?.wasTruncated),
    [inlineReviewData]
  );
  const [scanRangeConfirmed, setScanRangeConfirmed] = useState(false);
  useEffect(() => {
    setScanRangeConfirmed(false);
  }, [inlineReviewData]);
  const needsScanRangeConfirm = scanRangeReview.needsConfirm && !scanRangeConfirmed;

  // 학생 수 상한 (서버 400 과 같은 값). 파일 경로는 Step2 를 건너뛰므로 여기서도 막는다.
  const isInlineOverStudentLimit = inlineStudents.length > SAENGGIBU_BATCH_MAX_STUDENTS;
  const {
    rosterId: inlineRosterId,
    onApply: handleInlineRosterApply,
    onRosterChange: handleInlineRosterChange,
  } = useClassRosterLink(setInlineStudents);
  const [inlinePriorSemester, setInlinePriorSemester] = useState(null);

  const handleStartGenerationFromInlineReview = useCallback(() => {
    if (!inlineReviewData || inlineStudents.length === 0) {
      showToast('생성할 학생 정보가 없어요.', 'error');
      return;
    }
    if (isAnalyzingReference) {
      showToast(`${referenceCopy.shortLabel} 분석이 끝난 뒤 만들어주세요.`, 'info');
      return;
    }
    if (referencePlan && !referenceFile) {
      showToast('평가계획서에서 이번 반에 쓸 평가를 먼저 골라주세요.', 'info');
      return;
    }
    if (isInlineOverStudentLimit) {
      showToast(`한 번에 ${SAENGGIBU_BATCH_MAX_STUDENTS}명까지 만들 수 있어요. 반별로 나눠 올려주세요.`, 'error');
      return;
    }
    if (needsScanRangeConfirm) {
      showToast('학생 수와 페이지 범위를 먼저 확인해주세요.', 'info');
      return;
    }

    if (inlineReviewData.classCategory) {
      const { revised, removed } = classEvidenceReviewCounts(inlineReviewData.analysisResult?.students, inlineStudents);
      trackSaenggibuUxEvent('saenggibu_class_evidence_reviewed', {
        saenggibu_category: options.category,
        is_batch: true,
        student_count: inlineStudents.length,
        revised_count: revised,
        removed_count: removed,
      });
    }
    const reviewedStudents = inlineStudents.map((student, index) => ({
      ...student,
      name: String(student.name || '').trim() || `학생 ${index + 1}`,
    }));
    const outgoingOptions = toOutgoingOptions(inlinePriorSemester?.targetBytes
      ? { ...options, wordCount: inlinePriorSemester.targetBytes }
      : options);

    onNext({
      ...inlineReviewData,
      metadata: {
        ...(inlineReviewData.metadata || {}),
        ...(inlineRosterId ? { roster_id: inlineRosterId } : {}),
      },
      analysisResult: {
        ...inlineReviewData.analysisResult,
        students: reviewedStudents
      },
      options: outgoingOptions,
      selectedOptions: outgoingOptions,
      skipToProcessing: true
    });
  }, [inlinePriorSemester, inlineReviewData, inlineRosterId, inlineStudents, isAnalyzingReference, isInlineOverStudentLimit, needsScanRangeConfirm, onNext, options, referenceCopy.shortLabel, referenceFile, referencePlan, showToast]);

  const handleDownloadInlineRecognition = useCallback(() => {
    if (inlineStudents.length === 0) {
      showToast('다운로드할 인식 내용이 없어요.', 'info');
      return;
    }

    try {
      const rows = buildRecognitionExportRows(inlineStudents, {
        contentColumnLabel: '인식된 학생 원문',
        getContent: getInlineStudentContent,
        getSourceFileName: student => (
          student.source_file_name
          || inlineReviewData?.fileName
          || inlineReviewData?.fileMeta?.filename
          || ''
        ),
      });
      const category = String(options.category || '생기부').replace(/[\\/:*?"<>|]/g, '_');
      writeRecognitionWorkbook(XLSX, rows, {
        filename: `${category}_인식결과_${getRecognitionExportDate()}.xlsx`,
        sheetName: '인식 결과',
      });
      showToast('인식 내용을 다운로드 폴더에 저장했어요.', 'success', 6000);
    } catch (error) {
      console.error('생기부 인식 내용 엑셀 다운로드 실패:', error);
      showToast(error.message || '인식 내용을 다운로드하지 못했어요.', 'error');
    }
  }, [getInlineStudentContent, inlineReviewData, inlineStudents, options.category, showToast]);

  const buildStepDataFromStudents = useCallback((students, sourceMeta = {}) => {
    const averageConfidence = students.length > 0
      ? Math.round((students.reduce((sum, student) => sum + (Number(student.confidence) || 0.8), 0) / students.length) * 100) / 100
      : 0;

    const structureAnalysis = {
      total_students: students.length,
      detected_columns: sourceMeta.detectedColumns || ['학생명', '관찰내용'],
      analysis_method: sourceMeta.analysisMethod || '붙여넣기 표 자동 분석',
      content_column_count: sourceMeta.contentColumnCount || (sourceMeta.detectedColumns || []).length || 1
    };

    const analysisResult = {
      structure_analysis: structureAnalysis,
      students,
      overall_quality: {
        average_confidence: averageConfidence,
        total_processable: students.length,
        recommendations: [
          `총 ${students.length}명의 학생 정보가 감지되었습니다.`,
          '생성 전에 학생별 내용을 확인해주세요.'
        ]
      }
    };

    const fileMeta = {
      source: sourceMeta.source || 'paste',
      ext: 'text',
      mime_type: 'text/plain',
      file_size: pasteText.length,
      detected_students: students.length,
      analysis_method: structureAnalysis.analysis_method
    };

    return {
      fileName: sourceMeta.fileName || '붙여넣기 입력',
      fileSize: pasteText.length,
      uploadTime: new Date().toISOString(),
      analysisResult,
      fileMeta,
      metadata: {
        source_file_meta: fileMeta,
        analysis_quality: analysisResult.overall_quality,
        structure_analysis: structureAnalysis
      },
      options: toOutgoingOptions(options)
    };
  }, [options, pasteText.length]);

  const handleDownloadTemplate = useCallback(() => {
    const ws = XLSX.utils.aoa_to_sheet(uploadGuide.templateRows);
    ws['!cols'] = [{ wch: 8 }, { wch: 12 }, { wch: 80 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '학생명렬표');
    XLSX.writeFile(wb, `${options.category}_학생기록_참고양식.xlsx`);
    showToast('업로드 양식을 다운로드했어요', 'success');
  }, [options.category, showToast, uploadGuide.templateRows]);

  const handleConfirmTemplateDownload = useCallback(() => {
    setShowTemplateGuide(false);
    handleDownloadTemplate();
  }, [handleDownloadTemplate]);

  const closeAssistGuide = useCallback(() => {
    setShowAssistModal(false);
  }, []);

  const handleFillExample = useCallback(() => {
    closeAssistGuide();
    const exampleText = uploadGuide.templateRows.map(row => row.join('\t')).join('\n');
    setPasteRows(buildGridRowsFromPastedText(exampleText));
    setPasteText(exampleText);
    setShowPastePanel(true);
    setPasteError(null);
    setUploadError(null);
    showToast('예시를 붙여넣기 칸에 채웠어요', 'success');
  }, [closeAssistGuide, showToast, uploadGuide.templateRows]);

  const handlePasteGrid = useCallback((e) => {
    const clipboardText = e.clipboardData?.getData('text/plain') || '';
    if (!clipboardText.trim()) return;
    if (!shouldHydrateGridFromClipboard(clipboardText)) return;

    e.preventDefault();
    const parsed = parseStudentsFromPastedTable(clipboardText);
    const nextRows = buildGridRowsFromPastedText(clipboardText);
    setPasteRows(nextRows);
    setPasteText(gridRowsToText(nextRows, uploadGuide.contentLabel));
    setPasteError(null);
    if (parsed.students.length > 0) {
      showToast(
        `학생 ${parsed.students.length}명, 참고 열 ${parsed.contentColumnCount || 1}개를 찾았어요.`,
        'success'
      );
    }
  }, [showToast, uploadGuide.contentLabel]);

  const handlePasteCellChange = useCallback((rowIndex, field, value) => {
    setPasteRows(prevRows => {
      const nextRows = prevRows.map((row, index) => (
        index === rowIndex ? { ...row, [field]: value } : row
      ));
      setPasteText(gridRowsToText(nextRows, uploadGuide.contentLabel));
      return nextRows;
    });
    setPasteError(null);
  }, [uploadGuide.contentLabel]);

  const handleAddPasteRow = useCallback(() => {
    setPasteRows(prevRows => ([
      ...prevRows,
      { no: String(prevRows.length + 1), name: '', content: '' }
    ]));
  }, []);

  const handleAnalyzePaste = useCallback(async () => {
    if (!isProUser) {
      setShowProModal(true);
      return;
    }

    if (!currentUser) {
      setPasteError('로그인이 필요해요.');
      return;
    }

    const textFromGrid = gridRowsToText(pasteRows, uploadGuide.contentLabel);
    const trimmedText = (textFromGrid || pasteText).trim();
    if (!trimmedText) {
      setPasteError('엑셀이나 나이스 표에서 복사한 내용을 붙여넣어주세요.');
      return;
    }

    try {
      setIsAnalyzingPaste(true);
      setPasteError(null);

      const parsed = parseStudentsFromPastedTable(trimmedText);
      if (parsed.students.length > 0) {
        onNext({
          ...buildStepDataFromStudents(parsed.students, {
            source: 'paste',
            detectedColumns: parsed.detectedColumns,
            analysisMethod: parsed.hasHeader ? '붙여넣기 표 자동 분석' : '붙여넣기 표 자동 추정',
            contentColumnCount: parsed.contentColumnCount
          }),
          selectedOptions: toOutgoingOptions(options),
          skipToProcessing: true
        });
        return;
      }

      const authToken = await currentUser.getIdToken();
      const analysisResult = await analyzePastedBatchText(trimmedText, options.category, authToken);
      const students = analysisResult.data?.students || [];
      if (students.length === 0) {
        setPasteError(`학생 이름과 ${uploadGuide.contentLabel}을 찾지 못했어요. 번호, 이름, ${uploadGuide.contentLabel}이 보이도록 붙여넣어주세요.`);
        return;
      }

      const metadata = {
        source_file_meta: {
          ...(analysisResult.file_meta || {}),
          source: 'paste',
          has_filename: false
        },
        analysis_quality: analysisResult.data?.overall_quality || {},
        structure_analysis: analysisResult.data?.structure_analysis || {}
      };

      onNext({
        fileName: '붙여넣기 입력',
        fileSize: trimmedText.length,
        uploadTime: new Date().toISOString(),
        analysisResult: analysisResult.data,
        fileMeta: analysisResult.file_meta,
        warnings: collectAnalysisWarnings(analysisResult),
        confidence: analysisResult.confidence,
        wasTruncated: Boolean(analysisResult.was_truncated),
        metadata,
        options: toOutgoingOptions(options),
        selectedOptions: toOutgoingOptions(options),
        skipToProcessing: true
      });
    } catch (error) {
      console.error('붙여넣기 분석 오류:', error);
      setPasteError(error.message || '붙여넣은 내용을 분석하지 못했어요.');
    } finally {
      setIsAnalyzingPaste(false);
    }
  }, [buildStepDataFromStudents, currentUser, isProUser, onNext, options, pasteRows, pasteText, uploadGuide.contentLabel]);

  const showBatchBottomCta = uploadedFile && !isUploading && inlineReviewData;
  const isBatchBottomCtaDisabled = isAnalyzingReference || inlineStudents.length === 0 || isInlineOverStudentLimit || needsScanRangeConfirm;
  const batchBottomCtaLabel = isAnalyzingReference
    ? referenceCopy.analyzingLabel
    : isInlineOverStudentLimit
      ? `${SAENGGIBU_BATCH_MAX_STUDENTS}명 이하로 줄여주세요 (지금 ${inlineStudents.length}명)`
      : needsScanRangeConfirm
        ? '학생 범위를 확인한 뒤 만들 수 있어요'
        : `${inlineStudents.length}명 생기부 문구 만들기`;

  return (
    <div className="saenggibu-batch-excel-upload">
      <input
        ref={referenceFileInputRef}
        type="file"
        accept={SUPPORTED_UPLOAD_ACCEPT}
        multiple
        onChange={handleReferenceFileSelect}
        style={{ display: 'none' }}
      />

      {/* 옵션 선택 영역 - 깔끔한 카드 스타일 (모바일 여백은 CSS 미디어쿼리에서 줄인다) */}
      <div className="saenggibu-batch-options-card">
        {/* 이어받은 설정 안내: 결과에서 돌아왔을 때 어떤 종류로 이어지는지 눈으로 확인하게 한다 */}
        {continuedFromPrevious && (
          <div
            className="saenggibu-batch-continue-chip"
            role="status"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexWrap: 'wrap',
              marginBottom: '12px',
              padding: '8px 12px',
              borderRadius: '10px',
              background: 'var(--whatquiz-blue-50, #eff6ff)',
              color: 'var(--whatquiz-primary, #3182f6)',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: 1.5,
            }}
          >
            <span>이전 설정으로 이어서 만들어요</span>
            <span style={{ color: 'var(--whatquiz-gray-600, #4e5968)', fontWeight: 500 }}>
              {[
                getBatchCategoryLabel(options.category),
                `${options.wordCount}B`,
                options.referenceText ? `${referenceCopy.shortLabel} 유지` : '',
                options.teacherInstruction ? '요청사항 유지' : '',
                (options.achievementStandards || []).length > 0 ? `성취기준 ${(options.achievementStandards || []).length}개` : '',
              ].filter(Boolean).join(' · ')}
            </span>
          </div>
        )}
        {/* 카테고리 선택 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--whatquiz-gray-500)', whiteSpace: 'nowrap' }}>종류</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', flex: 1 }}>
            {BATCH_SUPPORTED_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  // 참고자료 성격이 달라지면(평가계획서 ↔ 동아리 계획서, 또는 참고자료 없음)
                  // 이전 자료를 그대로 두면 엉뚱한 기준으로 생성된다.
                  const keepReference = (
                    getBatchReferenceKind(cat) === getBatchReferenceKind(options.category)
                    && getBatchReferenceKind(cat) !== null
                  );
                  setOptions(prev => ({
                    ...(keepReference ? prev : withoutPlanPick(prev)),
                    category: cat,
                    ...(keepReference ? {} : { referenceText: '', referenceFiles: [] }),
                    ...((cat === '교과' || cat === '교과_고등') ? {} : { achievementStandards: [] }),
                  }));
                  if (cat !== '교과' && cat !== '교과_고등') {
                    setIsAchievementPickerOpen(false);
                  }
                  if (!keepReference) {
                    setReferenceFile(null);
                    setReferenceError(null);
                    setReferencePlan(null);
                  }
                  // 확인 대기 중인 카드는 이전 카테고리 문구라 항상 닫는다.
                  setReferenceReview(null);
                  // 인식한 학생 목록은 유지한다. 예전에는 여기서 비워서 파일을 다시 올려야 했고,
                  // 프로덕션에서 종류 사고 → 재업로드 왕복이 반복됐다. 대신 안내를 띄운다.
                  if (inlineStudents.length > 0) {
                    const sameMemos = classCategory && memoCategoryOf(cat) === memoCategoryOf(classCategory);
                    setCategorySwitchNotice(sameMemos ? null : {
                      from: options.category,
                      to: cat,
                      count: inlineStudents.length,
                      ...(classCategory ? { classCategory } : {}),
                    });
                    trackSaenggibuUxEvent('saenggibu_batch_category_switch_after_upload', {
                      saenggibu_category: cat,
                      from_category: options.category,
                      student_count: inlineStudents.length,
                      is_batch: true,
                    });
                  }
                }}
                disabled={isUploading}
                style={{
                  flex: '1 1 72px',
                  minWidth: '72px',
                  padding: '8px 10px',
                  fontSize: '13px',
                  fontWeight: options.category === cat ? '600' : '500',
                  color: options.category === cat ? 'var(--whatquiz-primary)' : 'var(--whatquiz-gray-500)',
                  background: options.category === cat ? 'var(--whatquiz-blue-50)' : 'var(--whatquiz-gray-50)',
                  border: options.category === cat ? '1.5px solid var(--whatquiz-primary)' : '1.5px solid transparent',
                  borderRadius: '8px',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.6 : 1,
                  transition: 'all 0.15s ease'
                }}
              >
                {getBatchCategoryLabel(cat)}
              </button>
            ))}
          </div>
        </div>

        {/* 바이트 선택 - 슬라이더 (단일 생성과 일관성) */}
        <div className="sgb-batch-byte-slider">
          <div className="sgb-batch-byte-header">
            <span className="sgb-batch-byte-label">목표 바이트</span>
            <span className="sgb-batch-byte-value">{options.wordCount}B</span>
          </div>
          <input
            type="range"
            aria-label="목표 바이트 수 설정"
            min="100"
            max={getMaxBytes(options.category)}
            step="100"
            value={options.wordCount}
            disabled={isUploading}
            onChange={(e) => setOptions(prev => ({ ...prev, wordCount: parseInt(e.target.value) }))}
            className="sgb-batch-range-slider"
            style={{
              background: `linear-gradient(to right, var(--whatquiz-primary) 0%, var(--whatquiz-primary) ${((options.wordCount - 100) / (getMaxBytes(options.category) - 100)) * 100}%, var(--whatquiz-gray-200) ${((options.wordCount - 100) / (getMaxBytes(options.category) - 100)) * 100}%, var(--whatquiz-gray-200) 100%)`
            }}
          />
          <div className="sgb-batch-byte-range">
            <span>100B</span>
            <span>{getMaxBytes(options.category)}B</span>
          </div>
        </div>

        <SelectedAchievementStandardsField
          category={options.category}
          notice={achievementRestoreNotice}
          onOpenPicker={() => setIsAchievementPickerOpen(true)}
          onRemove={(record) => setOptions((prev) => ({
            ...prev,
            achievementStandards: (prev.achievementStandards || []).filter(
              (item) => recordIdentity(item) !== recordIdentity(record),
            ),
          }))}
          selectedStandards={options.achievementStandards || []}
        />
      </div>

      {/* 파일 업로드 영역 - 깔끔한 디자인 */}
      <div
        className={`saenggibu-batch-excel-drop-zone ${isDragOver ? 'drag-over' : ''} ${uploadedFile ? 'uploaded' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!uploadedFile && !isUploading ? handleSelectClick : undefined}
        style={{ 
          minHeight: uploadedFile ? '80px' : '160px',
          padding: uploadedFile ? '16px 20px' : '32px 24px',
          marginBottom: '20px',
          cursor: !uploadedFile && !isUploading ? 'pointer' : 'default'
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={SUPPORTED_UPLOAD_ACCEPT}
          onClick={(e) => e.stopPropagation()}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        {!uploadedFile ? (
          <div className="saenggibu-batch-drop-zone-content" style={{ gap: '12px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '12px', 
              backgroundColor: 'var(--whatquiz-blue-50)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'var(--whatquiz-primary)' }} strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            
            <div className="saenggibu-batch-drop-guide">
              <p className="saenggibu-batch-drop-title">
                {uploadGuide.title}
                <FieldHelpTooltip
                  title={BATCH_HELP_TEXT.upload(uploadGuide).title}
                  items={BATCH_HELP_TEXT.upload(uploadGuide).items}
                  ariaLabel="다중 생성 파일 안내"
                  variant="compact"
                />
              </p>
              <p className="saenggibu-batch-drop-subtitle">
                양식을 맞출 필요 없어요. 학생 이름이 있으면 나머지 열을 참고 내용으로 읽어요.
              </p>
              <div className="saenggibu-batch-drop-examples" aria-label="예시 자료">
                {uploadGuide.examples.map((example) => (
                  <span key={example}>{example}</span>
                ))}
              </div>
              <p className="saenggibu-batch-drop-format">
                Excel, PDF, PPT, 한글, 이미지 · 최대 50MB
              </p>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '36px', 
              height: '36px', 
              borderRadius: '10px', 
              backgroundColor: 'var(--whatquiz-green-50)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'var(--whatquiz-success)' }} strokeWidth="2.5">
                <polyline points="20,6 9,17 4,12"/>
              </svg>
            </div>
            
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--whatquiz-gray-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {uploadedFile.fileName}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--whatquiz-gray-500)' }}>
                {uploadedFile.fileSize > 0
                  ? `${(uploadedFile.fileSize / 1024 / 1024).toFixed(2)} MB`
                  : `학생 ${inlineStudents.length}명`}
              </div>
            </div>
            {!isUploading && (
              <button
                type="button"
                className="saenggibu-batch-replace-file-btn"
                onClick={handleReplaceUploadedFile}
                aria-label="다른 파일로 바꾸기"
                style={{
                  flexShrink: 0,
                  padding: '7px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--whatquiz-gray-600)',
                  background: 'var(--whatquiz-gray-50)',
                  border: '1px solid var(--whatquiz-gray-200)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                파일 바꾸기
              </button>
            )}
          </div>
        )}

      </div>

      {!uploadedFile && !isUploading && isScoringImportCategory && hasScoringRecords && (
        <div className="saenggibu-batch-scoring-import">
          <span>채점한 서술형 평가로 세특을 쓸 수 있어요</span>
          <button
            type="button"
            data-testid="batch-scoring-import-open"
            onClick={() => setIsScoringImportOpen(true)}
          >
            채점 결과 불러오기
          </button>
        </div>
      )}

      {/* 🆕 에러 표시 UI */}
      {uploadError && !isUploading && (
        <div style={{
          backgroundColor: 'var(--whatquiz-red-50)',
          border: '1px solid var(--whatquiz-red-200)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: 'var(--whatquiz-red-100)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'var(--whatquiz-red-600)' }} strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ 
                fontSize: '15px', 
                fontWeight: '600', 
                color: 'var(--whatquiz-red-600)', 
                margin: '0 0 8px 0' 
              }}>
                {uploadError.title}
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--whatquiz-red-700)', 
                margin: 0,
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap'
              }}>
                {uploadError.detail}
              </p>
            </div>
          </div>
          <button
            onClick={() => setUploadError(null)}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              backgroundColor: 'var(--whatquiz-red-600)',
              color: 'var(--whatquiz-white, #fff)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--whatquiz-red-700)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--whatquiz-red-600)';
            }}
          >
            확인
          </button>
        </div>
      )}

      {/* 업로드 진행률 */}
      {isUploading && (
        <div style={{
          backgroundColor: 'var(--whatquiz-gray-50, #f9fafb)',
          border: 'none',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
        }}>
          <div className="saenggibu-batch-upload-progress-head">
            <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--whatquiz-gray-800)' }}>
              {progressMessage || '학생 정보를 분석하고 있어요'}
            </span>
            <button
              type="button"
              className="saenggibu-batch-upload-cancel-btn"
              onClick={handleCancelFileAnalysis}
            >
              분석 중단
            </button>
            {/* 🆕 % 표시 제거 - 큰 파일의 경우 95%에서 오래 대기하는 UX 문제 해결 */}
          </div>
          <div style={{ 
            height: '6px', 
            backgroundColor: 'var(--whatquiz-gray-200)', 
            borderRadius: '100px', 
            overflow: 'hidden' 
          }}>
            <div 
              role="progressbar"
              aria-valuenow={uploadProgress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="파일 분석 진행률"
              style={{ 
                width: `${uploadProgress}%`,
                height: '100%',
                backgroundColor: 'var(--whatquiz-primary)',
                transition: 'width 0.3s ease',
                borderRadius: '100px'
              }}
            />
          </div>
          <p style={{ fontSize: '13px', color: 'var(--whatquiz-gray-500)', margin: '12px 0 0 0', textAlign: 'center' }}>
            {remainingTime > 0 
              ? `약 ${remainingTime}초 남음` 
              : '거의 완료'}
          </p>
        </div>
      )}

      {uploadedFile && !isUploading && inlineReviewData && (
        <div className="saenggibu-batch-inline-review">
          <div className="saenggibu-batch-inline-review-header">
            {classCategory ? (
              <div data-testid="batch-class-records-header">
                <strong>{inlineStudents.length}명 기록을 학급 관리에서 가져왔어요</strong>
                <span>{categoryLabelOf(classCategory)} 메모를 한 줄에 하나씩 넣었어요. 넣고 싶지 않은 줄은 지우면 돼요.</span>
                {inlineReviewData.classTargetBytes < CLASS_TARGET_MAX_BYTES && Number(options.wordCount) === inlineReviewData.classTargetBytes && (
                  <span data-testid="batch-class-target-note">
                    {`메모 양에 맞춰 ${inlineReviewData.classTargetBytes}B로 잡았어요. 더 길게 하면 메모에 없는 내용이 들어갈 수 있어요.`}
                  </span>
                )}
              </div>
            ) : (
              <div>
                <strong>{inlineStudents.length}명 학생 정보를 찾았어요</strong>
                <span>이름과 원문을 한눈에 보고, 다른 부분은 입력칸에서 바로 고쳐주세요.</span>
              </div>
            )}
            <button
              type="button"
              className="saenggibu-batch-inline-download"
              onClick={handleDownloadInlineRecognition}
            >
              인식 내용 엑셀로 받기
            </button>
          </div>

          {categorySwitchNotice && (
            <div
              className="saenggibu-batch-category-switch-notice"
              role="status"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                flexWrap: 'wrap',
                margin: '0 0 12px',
                padding: '10px 14px',
                borderRadius: '10px',
                background: 'var(--whatquiz-blue-50, #eff6ff)',
                color: 'var(--whatquiz-gray-700, #333d4b)',
                fontSize: '13px',
                lineHeight: 1.5,
              }}
            >
              {categorySwitchNotice.classCategory ? (
                <span data-testid="batch-class-category-notice">
                  <strong style={{ color: 'var(--whatquiz-primary)' }}>{getBatchCategoryLabel(categorySwitchNotice.to)}</strong>
                  {`(으)로 바꿨어요. 학생 글은 학급 관리에서 ${categoryLabelOf(categorySwitchNotice.classCategory)}에 맞춰 고른 메모예요. 이 영역 메모로 쓰려면 학급 관리에서 영역을 바꿔 다시 가져와 주세요.`}
                </span>
              ) : (
                <span>
                  <strong style={{ color: 'var(--whatquiz-primary)' }}>{getBatchCategoryLabel(categorySwitchNotice.to)}</strong>
                  {'(으)로 바꿨어요. 인식한 학생 '}
                  {categorySwitchNotice.count}
                  {'명은 그대로 이어서 만들어요. 종류에 맞게 다시 인식하려면 파일을 바꿔주세요.'}
                </span>
              )}
              <button
                type="button"
                onClick={() => setCategorySwitchNotice(null)}
                aria-label="안내 닫기"
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'var(--whatquiz-gray-500)',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '2px 4px',
                }}
              >
                닫기
              </button>
            </div>
          )}

          {(options.referenceText || (options.referenceFiles || []).length > 0 || options.teacherInstruction) && (
            <div className="saenggibu-batch-inline-review-badges">
              {(options.referenceText || (options.referenceFiles || []).length > 0) && <span>{referenceCopy.badge}</span>}
              {options.teacherInstruction && <span>요청사항 반영</span>}
            </div>
          )}

          {isInlineOverStudentLimit && (
            <div className="saenggibu-batch-inline-notice saenggibu-batch-inline-notice--limit" role="alert">
              <strong>한 번에 {SAENGGIBU_BATCH_MAX_STUDENTS}명까지 생성할 수 있어요</strong>
              <span>
                지금 {inlineStudents.length}명이에요. {inlineStudents.length - SAENGGIBU_BATCH_MAX_STUDENTS}명을 제외하거나, 반별로 나눠서 올려주세요.
                나눠 올려도 결과는 생성 기록에 각각 저장돼요.
              </span>
            </div>
          )}

          {readGapMessages.length > 0 && (
            <div className="saenggibu-batch-inline-notice saenggibu-batch-inline-notice--gap" role="alert">
              <strong>파일 일부를 읽지 못했어요</strong>
              {readGapMessages.map((message) => (
                <span key={message}>{message}</span>
              ))}
            </div>
          )}

          {scanRangeReview.needsConfirm && (
            <div className="saenggibu-batch-inline-notice saenggibu-batch-inline-notice--range" role="alert">
              <strong>학생 범위를 확인해주세요</strong>
              <span>
                {scanRangeReview.reasons.join(' ')}
                {' '}
                {scanRangeReview.pageCount > 0 ? `${scanRangeReview.pageCount}쪽을 ${inlineStudents.length}명으로 나눴어요. ` : ''}
                각 학생 카드의 쪽 번호와 원문을 확인하고, 학생이 아닌 페이지는 <b>제외</b>를 눌러주세요.
              </span>
              <label className="saenggibu-batch-inline-confirm">
                <input
                  type="checkbox"
                  checked={scanRangeConfirmed}
                  onChange={(e) => {
                    setScanRangeConfirmed(e.target.checked);
                    if (e.target.checked) {
                      trackSaenggibuUxEvent('batch_scan_range_confirmed', {
                        students: inlineStudents.length,
                        pages: scanRangeReview.pageCount,
                        range_source: scanRangeReview.rangeSource,
                        confidence: scanRangeReview.confidence,
                      });
                    }
                  }}
                />
                <span>학생 수와 페이지 범위를 확인했어요</span>
              </label>
            </div>
          )}

          <BatchDuplicateStudentCard
            key={`duplicate-${inlineReviewData.uploadTime || inlineReviewData.fileName || 'inline-review'}`}
            students={inlineStudents}
            onStudentsChange={setInlineStudents}
            category={options.category}
          />

          <ClassRosterCard
            key={inlineReviewData.uploadTime || inlineReviewData.fileName || 'inline-review'}
            students={inlineStudents}
            onApply={handleInlineRosterApply}
            onRosterChange={handleInlineRosterChange}
          />
          <PriorSemesterOption
            rosterId={inlineRosterId}
            students={inlineStudents}
            category={options.category}
            schoolLevel={options.schoolLevel || '중등'}
            wordCount={options.wordCount}
            onChange={setInlinePriorSemester}
          />

          <StudentRecognitionReviewList
            students={inlineStudents}
            getContent={getInlineStudentContent}
            getColumns={student => student.included_columns || []}
            getMetaItems={student => {
              const pages = formatSourcePages(student.source_pages);
              return [...(pages ? [pages] : []), ...getMergedMetaItems(student)];
            }}
            onNameChange={handleInlineStudentNameChange}
            onContentChange={handleInlineStudentContentChange}
            onRemove={handleInlineStudentRemove}
            contentLabel="AI가 참고할 원문"
            contentPlaceholder="학생 자료에서 읽은 내용을 확인하고 필요하면 바로 수정해주세요."
          />
        </div>
      )}

      <div className="saenggibu-batch-smart-options">
        {inlineReviewData && (
          <p className="saenggibu-batch-final-options-note">
            생성 직전까지 참고자료와 요청사항을 수정할 수 있어요.
          </p>
        )}
        {isReferenceCategory && (
          <details className={`saenggibu-batch-option-card ${isReferenceDragOver ? 'reference-drag-over' : ''}`}>
              <summary>
                <span>
                  {referenceCopy.cardTitle}
                  <FieldHelpTooltip
                    title={referenceCopy.title}
                    items={referenceCopy.items}
                    ariaLabel={`${referenceCopy.cardTitle} 안내`}
                    variant="compact"
                  />
                </span>
                <small>선택</small>
              </summary>
              <div
                className="saenggibu-batch-option-body"
                onDragOver={handleReferenceDragOver}
                onDragLeave={handleReferenceDragLeave}
                onDrop={handleReferenceDrop}
              >
                {referenceFile ? (
                  <div className="saenggibu-batch-reference-file">
                    <div>
                      <strong>{referenceFile.name}</strong>
                      <span>
                        {referenceFile.source === LIBRARY_SOURCE
                          ? '자료함 · 분석 확인 완료'
                          : `${(referenceFile.size / 1024 / 1024).toFixed(2)} MB · 분석 확인 완료`}
                      </span>
                      {Array.isArray(referenceFile.files) && referenceFile.files.length > 1 && (
                        <span>{referenceFile.files.map(file => file.name).join(', ')}</span>
                      )}
                      {referenceFile.planLabel && (
                        <span className="saenggibu-batch-reference-plan-label">고른 평가 · {referenceFile.planLabel}</span>
                      )}
                    </div>
                    <div className="saenggibu-batch-reference-file-actions">
                      {referencePlan && (
                        <button type="button" onClick={handleChangeReferencePlanItem}>다른 평가로 바꾸기</button>
                      )}
                      <button type="button" onClick={handleRemoveReferenceFile}>삭제</button>
                    </div>
                  </div>
                ) : referencePlan ? (
                  <div className="saenggibu-batch-plan-pick" role="group" aria-label="반 전체에 쓸 평가 고르기">
                    <p className="saenggibu-batch-plan-pick-title">
                      평가 {referencePlan.items.length}개를 찾았어요. 이번 반에 쓸 평가를 골라주세요
                    </p>
                    <div className="saenggibu-batch-plan-pick-chips">
                      {referencePlan.names.map((name, index) => (
                        <button
                          type="button"
                          key={`${name}-${index}`}
                          onClick={() => handlePickReferencePlanItem(index)}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                    <div className="saenggibu-batch-plan-pick-actions">
                      <button type="button" onClick={() => handlePickReferencePlanItem('all')}>
                        모두 합쳐서 쓰기
                      </button>
                      <button type="button" onClick={handleRemoveReferenceFile}>다른 파일 올리기</button>
                    </div>
                  </div>
                ) : (
                  /* 📚 자료함에 자료가 있으면 업로드 버튼 클릭이 "파일 올리기 / 자료함에서 고르기" 메뉴 (Phase 4c); 없으면 바로 파일 선택 */
                  <SaenggibuAttachMenu
                    currentUser={currentUser}
                    category={options.category}
                    isBatch
                    libraryEnabled={!isAnalyzingReference}
                    disabled={isAnalyzingReference || isUploading}
                    onPick={addReferenceFromLibrary}
                    onUploadClick={() => referenceFileInputRef.current?.click()}
                    renderTrigger={(triggerProps) => (
                      <button
                        type="button"
                        className="saenggibu-batch-inline-upload"
                        {...triggerProps}
                        disabled={isAnalyzingReference || isUploading}
                      >
                        {isAnalyzingReference ? referenceCopy.analyzingLabel : referenceCopy.uploadLabel}
                      </button>
                    )}
                  />
                )}
                <textarea
                  className="saenggibu-batch-reference-textarea"
                  value={options.referenceText || ''}
                  onChange={(e) => handleReferenceTextChange(e.target.value)}
                  placeholder={referenceCopy.placeholder}
                  rows={3}
                  disabled={isAnalyzingReference || isUploading}
                />
                {referenceError && (
                  <div className="saenggibu-batch-paste-error">{referenceError}</div>
                )}
              </div>
          </details>
        )}

        <details className="saenggibu-batch-option-card">
            <summary>
              <span>
                AI에게 원하는 방향
                <FieldHelpTooltip
                  title={INSTRUCTION_HELP_TEXT.title}
                  items={INSTRUCTION_HELP_TEXT.items}
                  ariaLabel="AI 요청사항 안내"
                  variant="compact"
                />
              </span>
              <small>선택</small>
            </summary>
            <div className="saenggibu-batch-option-body">
              <textarea
                className="saenggibu-batch-instruction-textarea"
                value={teacherInstruction}
                onChange={(e) => handleTeacherInstructionChange(e.target.value)}
                placeholder={'예: 학생별 차이가 잘 드러나게 써줘.\n너무 과장하지 말고 담백하게, 수업 중 실제 행동 중심으로 써줘.\n좋은 세특처럼 탐구 과정과 근거가 자연스럽게 보이게 해줘.'}
                rows={5}
                disabled={isUploading}
              />
              <div className="saenggibu-batch-preset-chips">
                {instructionPresets.map((preset) => (
                  <button
                    type="button"
                    key={preset.label}
                    onClick={() => handleInstructionPreset(preset)}
                    title={preset.instruction}
                    disabled={isUploading}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
        </details>
      </div>

      {!inlineReviewData && (
        <div className="saenggibu-batch-assist">
          <button
            type="button"
            onClick={() => setShowAssistModal(true)}
            className="saenggibu-batch-assist-trigger"
            disabled={isUploading}
          >
            처음이라 막막하신가요?
          </button>
        </div>
      )}

      {!inlineReviewData && showPastePanel && (
        <div className="saenggibu-batch-paste-panel">
          <div className="saenggibu-batch-paste-guide">
            <strong>
              엑셀 표 전체 붙여넣기
              <FieldHelpTooltip
                title={BATCH_HELP_TEXT.paste(uploadGuide).title}
                items={BATCH_HELP_TEXT.paste(uploadGuide).items}
                ariaLabel="엑셀 표 붙여넣기 안내"
                variant="compact"
              />
            </strong>
            <span>아래 3열은 간단 예시예요. 실제 표는 열이 여러 개여도 그대로 붙여넣으세요.</span>
          </div>
          <div className="saenggibu-batch-sheet-editor" onPaste={handlePasteGrid}>
            <div className="saenggibu-batch-sheet-row header">
              <span>번호</span>
              <span>학생명</span>
              <span>{uploadGuide.contentLabel}</span>
            </div>
            {pasteRows.map((row, index) => (
              <div className="saenggibu-batch-sheet-row editable" key={`paste-row-${index}`}>
                <input
                  value={row.no}
                  onChange={(e) => handlePasteCellChange(index, 'no', e.target.value)}
                  aria-label={`${index + 1}번 행 번호`}
                  disabled={isAnalyzingPaste}
                />
                <input
                  value={row.name}
                  onChange={(e) => handlePasteCellChange(index, 'name', e.target.value)}
                  placeholder={index === 0 ? '김왓퀴' : ''}
                  aria-label={`${index + 1}번 행 학생명`}
                  disabled={isAnalyzingPaste}
                />
                <textarea
                  value={row.content}
                  onChange={(e) => handlePasteCellChange(index, 'content', e.target.value)}
                  placeholder={index === 0 ? `${uploadGuide.contentLabel}을 표 안에 그대로 붙여넣으세요` : ''}
                  aria-label={`${index + 1}번 행 ${uploadGuide.contentLabel}`}
                  disabled={isAnalyzingPaste}
                  rows={1}
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            className="saenggibu-batch-add-row-btn"
            onClick={handleAddPasteRow}
            disabled={isAnalyzingPaste}
          >
            행 추가하기
          </button>
          {pasteError && (
            <div className="saenggibu-batch-paste-error">
              {pasteError}
            </div>
          )}
          <div className="saenggibu-batch-paste-actions">
            <button type="button" className="secondary" onClick={handleFillExample} disabled={isAnalyzingPaste}>
              예시 채우기
            </button>
            <button type="button" className="primary" onClick={handleAnalyzePaste} disabled={isAnalyzingPaste}>
              {isAnalyzingPaste ? (
                '생성 준비 중...'
              ) : (
                <>
                  {!isProUser && <span className="saenggibu-batch-pro-badge">PRO</span>}
                  {!isProUser && ' '}
                  {isProUser ? '바로 생성하기' : 'Pro로 바로 생성하기'}
                </>
              )}
            </button>
          </div>
          <p className="saenggibu-batch-paste-note">
            {isProUser
              ? '붙여넣은 표를 바로 생기부 문구로 만들어요.'
              : '다중 생성은 Pro에서 이용할 수 있어요. 누르면 Pro 안내를 보여드려요.'}
          </p>
        </div>
      )}

      {showBatchBottomCta && (
        <>
          <div className="saenggibu-batch-bottom-cta-spacer" aria-hidden="true" />
          <div className="saenggibu-batch-bottom-cta saenggibu-batch-bottom-cta--dock">
            <button
              type="button"
              onClick={handleStartGenerationFromInlineReview}
              disabled={isBatchBottomCtaDisabled}
            >
              {batchBottomCtaLabel}
            </button>
          </div>
        </>
      )}

      {/* 🔍 파일 사전 검사 모달 */}
      <FilePreCheckModal
        isOpen={showPreCheckModal}
        onClose={handlePreCheckCancel}
        onConfirm={handlePreCheckConfirm}
        checkResult={preCheckResult}
        isLoading={preCheckLoading}
      />

      {/* PRO 업그레이드 모달 */}
      <ProUpgradeModal
        isOpen={showProModal}
        onClose={() => setShowProModal(false)}
        feature="학급 전체 생성"
      />

      <SaenggibuAnalysisReviewModal
        isOpen={Boolean(referenceReview)}
        review={referenceReview}
        onClose={() => setReferenceReview(null)}
        onReset={() => {
          setReferenceReview(null);
          // 평가를 고르던 중이면 목록으로 돌아간다.
          if (!referencePlan) referenceFileInputRef.current?.click();
        }}
        onConfirm={handleConfirmReferenceReview}
      />

      {/* 처음 사용자 도움말 — 어떤 파일을 올리면 되는지 먼저 알리고, 예시 채우기가 primary */}
      <Dialog
        closeLabel="도움말 닫기"
        description={uploadGuide.assistDescription}
        footer={(
          <ModalActions
            primary={{ label: '예시 보기', onClick: handleFillExample }}
            secondary={{
              label: '참고용 양식 받기',
              onClick: () => {
                closeAssistGuide();
                setShowTemplateGuide(true);
              },
            }}
            testId="batch-assist-actions"
          />
        )}
        onClose={closeAssistGuide}
        open={showAssistModal}
        size="md"
        testId="batch-assist-guide"
        title={uploadGuide.assistTitle}
      >
        <Notice className="saenggibu-batch-assist-explain" title="이렇게 쓰면 돼요">
          <p>학생 이름이 들어간 표나 파일을 그대로 올려주세요.</p>
          <p>열 이름은 달라도 괜찮아요. AI가 학생별 참고 내용으로 묶어요.</p>
          {isReferenceCategory && (
            <p>{referenceCopy.shortLabel}는 아래 선택 카드에 따로 넣으면 기준 자료로만 참고해요.</p>
          )}
        </Notice>
      </Dialog>

      {/* 참고용 양식 안내 — 양식 없이도 내 파일을 그대로 올릴 수 있다는 점을 먼저 알린다 */}
      <Dialog
        description={`이미 갖고 있는 ${uploadGuide.contentLabel}, 엑셀 파일, 한글·PDF 자료가 있다면 그대로 올려주세요. 빈 양식은 처음 시작할 때 참고용으로만 쓰면 돼요.`}
        footer={(
          <ModalActions
            primary={{ label: '참고용 양식 받기', onClick: handleConfirmTemplateDownload }}
            secondary={{ label: '내 파일 그대로 올릴게요', onClick: () => setShowTemplateGuide(false) }}
            testId="batch-template-guide-actions"
          />
        )}
        onClose={() => setShowTemplateGuide(false)}
        open={showTemplateGuide}
        size="md"
        testId="batch-template-guide"
        title="이 양식에 꼭 맞출 필요는 없어요"
      />

      <ScoringImportModal
        open={isScoringImportOpen}
        isPro={isProUser}
        onClose={() => setIsScoringImportOpen(false)}
        onImport={handleScoringImport}
      />

      <AchievementStandardPicker
        initialSelectedKeys={(options.achievementStandards || []).map(recordIdentity)}
        isOpen={isAchievementPickerOpen}
        onClose={() => setIsAchievementPickerOpen(false)}
        onInsert={(records) => {
          setOptions((prev) => ({
            ...prev,
            achievementStandards: mergeSelectedStandards(prev.achievementStandards, records).map(toRecordSummary),
          }));
          setIsAchievementPickerOpen(false);
        }}
        onSchoolLevelChange={options.category === '교과_고등' ? undefined : setPickerSchoolLevel}
        schoolLevel={achievementPickerSchoolLevel}
      />
    </div>
  );
};

export default Step1_ExcelUpload;